// src/validation/validators.js
// Validator engine for the “Validate” tab with compact ORTB output.
// Context ↔ Ad request linkage errors are surfaced via server issues, dvCheck, and mobianCheck.

import { validateIU, pageFromUrl, detectApp } from '../config/adUnitTaxonomy';
import { lookupPageOverride, urlToKey } from '../utils/pageOverrides';
import { runKBRules } from './RuleEngine';
import { runRuleSet } from './ruleSchema';
import { getActiveProfile, STANDARD_RULESET } from '../config/appProfiles';
import { loadBaseline } from '../utils/captureSession';
import { bodyState } from '../utils/bodyState';

//////////////////////////// helpers ////////////////////////////

// Exported (WP4a) so frontend/src/validation/issueSeverityParity.test.js can
// diff this against the CJS port at proxy-server/validation/issueSeverity.js —
// no behavior change, this function already took no browser-only input.
export function proxyIssuesChecks(req) {
    const out = [];
    const raw =
        (Array.isArray(req?.issues) && req.issues) ||
        (Array.isArray(req?.serverReport?.issues) && req.serverReport.issues) ||
        (Array.isArray(req?.validData?.issues) && req.validData.issues) ||
        [];
    raw.forEach((it, i) => {
        if (!it) return;

        // Extract human-readable text. Issue objects from different proxy validators
        // use different field names: Prebid uses .note, GAM uses .message, OpenRTB uses
        // .message, IMA uses .msg. Prefer the explicit text field over JSON.stringify.
        const text = typeof it === "string"
            ? it
            : (it.note || it.msg || it.message || it.details || it.label || JSON.stringify(it));

        // Respect the explicit level/severity set by the proxy validator.
        // Do NOT re-derive severity from keyword-scanning the message text — that
        // causes warnings containing the word "missing" to be wrongly elevated to fail.
        const rawLevel = String(it.level || it.severity || '').toLowerCase();
        let sev;
        if (rawLevel === 'pass' || rawLevel === 'ok' || rawLevel === 'success') {
            sev = 'pass';
        } else if (rawLevel === 'recommend') {
            // WP1 'recommend' severity tier (docs/signals-recommendations-watch-spec.md §4) —
            // a distinct noun-form tier stamped by capture-time burst detection, NOT the same
            // as the pre-existing adjective 'recommended' (which maps to 'warn' below) or the
            // 'recommendation' keyword (which maps to 'info' below). Gets its own 'rec' kind so
            // it can never be folded into error/warning rollups.
            sev = 'rec';
        } else if (rawLevel === 'info' || rawLevel === 'information' || rawLevel === 'recommendation' || rawLevel === 'notice') {
            // Informational recommendations (e.g. IMA app_version / npa / sdk_name "not present")
            // are advisory — never elevate them to warnings.
            sev = 'info';
        } else if (rawLevel === 'optional') {
            // Proxy 'optional' means the signal is adoption-dependent / expected to be
            // absent in some valid configs (e.g. GAM hb_* not forwarded when Amazon/AdX/
            // Direct wins) — advisory only, must not render as a warning.
            sev = 'info';
        } else if (rawLevel === 'warn' || rawLevel === 'warning' || rawLevel === 'recommended') {
            sev = 'warn';
        } else if (rawLevel === 'fail' || rawLevel === 'error' || rawLevel === 'required') {
            sev = 'fail';
        } else {
            // No explicit level — fall back to keyword scan only as last resort
            sev = /missing|error|invalid|unknown|mismatch|denied/i.test(text) ? 'fail' : 'warn';
        }

        // Surface the validator's `recommendation` (and category) as a hint so the
        // proxy card explains *why* each item matters instead of a terse one-liner.
        const hint = (it && typeof it === 'object')
            ? (it.recommendation || it.hint || undefined)
            : undefined;
        out.push({
            id: `proxy_${i}`,
            label: (it && (it.label || it.code || it.field)) || "Proxy Issue",
            severity: sev,
            message: text,
            ...(hint ? { hint } : {}),
        });
    });
    return out;
}

const decodePlusURIComponent = (s = "") => {
    try { return decodeURIComponent(String(s).replace(/\+/g, "%20")); }
    catch { return String(s || ""); }
};

const parseKvString = (s = "") => {
    const out = {};
    if (!s) return out;
    for (const part of s.split("&")) {
        if (!part) continue;
        const eq = part.indexOf("=");
        const k = eq >= 0 ? part.slice(0, eq) : part;
        const v = eq >= 0 ? part.slice(eq + 1) : "";
        if (!k) continue;
        if (out[k] != null) Array.isArray(out[k]) ? out[k].push(v) : (out[k] = [out[k], v]);
        else out[k] = v;
    }
    return out;
};

const headerValue = (headers = {}, name = "") => {
    if (!headers) return undefined;
    const n = String(name || "").toLowerCase();
    if (Array.isArray(headers)) {
        const found = headers.find((h) => (h?.name || h?.key || "").toLowerCase() === n);
        return found?.value ?? found?.val ?? undefined;
    }
    for (const k of Object.keys(headers)) {
        if (k.toLowerCase() === n) return headers[k];
    }
    return undefined;
};

const asText = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") {
        if (v.__binary || v._binary || v.base64) return "__BINARY__";
        try { return JSON.stringify(v); } catch { return String(v); }
    }
    try { return String(v); } catch { return ""; }
};

const looksBinaryString = (s) => typeof s === "string" && s.startsWith("__BINARY__");

const safeJson = (txt) => {
    if (txt == null || txt === "") return { ok: false, value: undefined, err: "empty" };
    if (typeof txt === "object") return { ok: true, value: txt, err: null };
    if (looksBinaryString(txt)) return { ok: false, value: undefined, err: "binary content" };
    try { return { ok: true, value: JSON.parse(txt), err: null }; }
    catch (e) { return { ok: false, value: undefined, err: e.message }; }
};

const qs = (url = "") => {
    try { return Object.fromEntries(new URL(url).searchParams.entries()); }
    catch { return {}; }
};

const isHttps = (url = "") => String(url || "").startsWith("https://");
const includes = (s = "", needle = "") => (s || "").toLowerCase().includes((needle || "").toLowerCase());
const hostFromUrl = (url = "") => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; } };
const isValidCCPA = (s) => /^[01Yy][NYny-][NYny-][NYny-]$/.test(s || "");
const toNum = (v, d = NaN) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

// tiny type helpers
const isStr = (v) => typeof v === "string";
const isNonEmptyStr = (v) => typeof v === "string" && v.length > 0;
const isNum = (v) => Number.isFinite(Number(v));
const isInt = (v) => Number.isInteger(Number(v));
const isArr = (v) => Array.isArray(v);
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const isArrOf = (a, pred) => Array.isArray(a) && a.every(pred);

// ---- Param shape helpers for GAM (query) ----
const isPkg = (s) => typeof s === "string" && /^[a-z0-9_.]+$/i.test(s);
const isSemverish = (s) => typeof s === "string" && /^\d+(\.\d+){1,3}$/.test(s);
const isVersionTag = (s) => typeof s === "string" && /[a-z0-9._-]/i.test(s) && s.length >= 3;
const isBoolStr = (s) => s === "0" || s === "1" || s === "true" || s === "false";
const isEnum = (s, arr) => arr.includes(String(s));
const isUrl = (s) => { try { new URL(String(s)); return true; } catch { return false; } };
// Accepts a plausible host or full URL — used for params that carry an
// app-scoped host (e.g. GAM's "url" query param), which may be a bare
// hostname or a full https:// URL depending on SDK.
const isHostish = (s) => {
    if (typeof s !== "string" || !s) return false;
    try { new URL(s); return true; } catch {}
    return /^[a-z0-9.-]+\.[a-z]{2,}/i.test(s);
};
const isCCPA = (s) => isValidCCPA(s);
const isSizesList = (s) => {
    if (!s) return false;
    const tokens = String(s).split(/[|,]/).map(t => t.trim()).filter(Boolean);
    return tokens.length > 0 && tokens.every(tok => /^[0-9]+x[0-9]+$/.test(tok));
};
const isUUIDish = (s) => typeof s === "string" && /^[0-9a-f-]{16,}$/i.test(s);
const isMccMnc = (s) => typeof s === "string" && /^[0-9]{5,6}$/.test(s);
const isLang2 = (s) => typeof s === "string" && /^[a-z]{2}$/i.test(s);
const isCountry2 = (s) => typeof s === "string" && /^[A-Z]{2}$/.test(s);
const isIntStr = (s) => Number.isInteger(Number(s));
const isNumStr = (s) => Number.isFinite(Number(s));
const isOrientation = (s) => isEnum(s, ["p", "l"]);
const isNet = (s) => isEnum(s, ["wi", "2g", "3g", "4g", "5g", "m"]);

// results
const mkGroup = (title, scope = "REQUEST") => ({ title, scope, results: [] });
const add = (g, id, label, severity, message) => g.results.push({ id, label, severity, message });

// quick skip helper
const addSkip = (g, id, label, reason) => add(g, id, label, "pass", `Skipped — ${reason}`);

// partner flags
const flags = (ctx) => {
    // APS gate: union of the profile's declared APS support and the context_api
    // flags (belt and braces) — a missing/false context field must not silently
    // blank the section when the active profile declares APS support.
    const profile = getActiveProfile();
    const profileHasAPS = !!(profile?.hasAPS || profile?.hasAPSVideo);
    const ctxHasAPS = !!(ctx?.context_api?.ad_partners?.aps_display || ctx?.context_api?.ad_partners?.aps_video);
    return {
        gma: !!(ctx?.context_api?.ad_partners?.gma),
        prebid: !!(ctx?.context_api?.ad_partners?.prebid),
        aps: profileHasAPS || ctxHasAPS,
    };
};

//////////////////////////// small compare helpers (for tables) ////////////////////////////

const splitList = (v) => {
    if (Array.isArray(v)) return v.map(String).map(s => s.trim()).filter(Boolean);
    if (v == null) return [];
    return String(v).split(",").map(s => s.trim()).filter(Boolean);
};
const cmpLists = (expected, found) => {
    const e = splitList(expected);
    const a = splitList(found);
    const eSet = new Set(e), aSet = new Set(a);
    const missing = e.filter(x => !aSet.has(x));
    const extra = a.filter(x => !eSet.has(x));
    return { missing, extra, ok: missing.length === 0 };
};
const fmtStatus = (ok) => ok ? "OK" : "Mismatch";
const mkMdRow = (cells) => `| ${cells.map(c => String(c ?? "—")).join(" | ")} |`;
const mkMdTable = (columns, rows) => {
    const head = mkMdRow(columns);
    const sep = `| ${columns.map(() => "---").join(" | ")} |`;
    const body = rows.map(r => mkMdRow(r)).join("\n");
    return `${head}\n${sep}\n${body}`;
};
const pushTableRow = (g, { id, label, severity, columns, rows, collapsed = false }) => {
    g.results.push({
        id,
        label,
        severity,
        message: "",
        table: { columns, rows },
        tableMd: mkMdTable(columns, rows),
        collapsed
    });
};

// generic deep search: find first property whose key matches (case-insensitive)
function findValueByKey(obj, key) {
    if (!obj || typeof obj !== "object") return undefined;
    const want = String(key).toLowerCase();
    const stack = [obj];
    while (stack.length) {
        const cur = stack.pop();
        if (cur && typeof cur === "object") {
            for (const k of Object.keys(cur)) {
                if (k.toLowerCase() === want) return cur[k];
                const v = cur[k];
                if (v && typeof v === "object") stack.push(v);
            }
        }
    }
    return undefined;
}

//////////////////////////// Common ////////////////////////////

function commonChecks(req) {
    const g = mkGroup("Common");

    const status = Number(req.status ?? req.responseCode ?? 0);
    const respText = asText(req.responseBody);
    const emptyBody = !respText || respText.trim().length === 0;

    // HTTP status: pass for 2xx, and also pass when body is empty & status is 0/204 (no-fill)
    let httpMsg = `Got ${status}`;
    let httpSeverity = "fail";
    if (status >= 200 && status < 300) {
        httpMsg = `Status ${status}`;
        httpSeverity = "pass";
    } else if (emptyBody && (status === 0 || status === 204)) {
        httpMsg = status === 0 ? "No content (no fill)" : "204 no content (no fill)";
        httpSeverity = "pass";
    }
    add(g, "status_code", "HTTP status 2xx", httpSeverity, httpMsg);

    // 204 must not have a body (keep as a soft check)
    if (status === 204) {
        const hasBody = !emptyBody;
        add(
            g,
            "no_content",
            "204 has no body",
            hasBody ? "warn" : "pass",
            hasBody ? "Body present on 204 (ignore content)" : "OK"
        );
    }

    // HTTPS
    add(
        g,
        "https",
        "Request over HTTPS",
        isHttps(req.url) ? "pass" : "warn",
        isHttps(req.url) ? "Secure" : "Not HTTPS"
    );

    // JSON parse: treat EMPTY as PASS (no-fill)
    // GAM /gampad/ads responses embed a full AFMA JS library + creative markup
    // inside ad_html (~40–80KB of minified JS). The proxy may truncate large
    // bodies before the full JSON is received, producing a parse error that is
    // an infrastructure artifact, not a real ad serving problem.
    // Rule: parse failure on a large body → warn (likely truncated by proxy);
    //       parse failure on a small body → fail (genuine malformed response).
    const ctype = (headerValue(req.responseHeaders, "content-type") || "").toLowerCase();
    if (ctype.includes("json")) {
        const parsed = safeJson(req.responseBody);
        if (!parsed.ok && parsed.err === "empty") {
            add(g, "json_parse", "Response JSON parse", "pass", "empty (no fill)");
        } else if (!parsed.ok) {
            const bodyLen = typeof req.responseBody === "string"
                ? req.responseBody.length
                : (req.responseBody ? JSON.stringify(req.responseBody).length : 0);
            // Bodies >20KB that fail to parse are almost certainly proxy truncation.
            // GAM ad_html embeds ~40-80KB of JS; truncation mid-string looks like
            // a parse error but the ad served correctly.
            const likelyTruncated = bodyLen > 20000;
            add(
                g,
                "json_parse",
                "Response JSON parse",
                likelyTruncated ? "warn" : "fail",
                likelyTruncated
                    ? `Body truncated by proxy (${(bodyLen/1024).toFixed(0)}KB captured) — ad_html too large to fully buffer`
                    : `Parse error: ${parsed.err}`
            );
        } else {
            add(g, "json_parse", "Response JSON parse", "pass", "Valid JSON");
        }
    }

    // Cache-Control
    const cc = headerValue(req.responseHeaders, "cache-control");
    if (cc) {
        const good = /no-cache|no-store|max-age=0/i.test(cc);
        add(g, "cache_control", "Cache-Control set", good ? "pass" : "info", cc);
    } else {
        add(g, "cache_control", "Cache-Control set", "info", "Missing");
    }

    // Latency
    if (req.durationMs != null) {
        add(
            g,
            "latency",
            "Latency",
            req.durationMs <= 1500 ? "pass" : "warn",
            `${Math.round(req.durationMs)} ms`
        );
    }

    // Binary body hint (skip for empty)
    if (!emptyBody && looksBinaryString(respText)) {
        add(g, "binary_body", "Response body is binary", "warn", "Non-text payload");
    }

    return g;
}

//////////////////////////// ORTB 2.6 (compact) ////////////////////////////

// Banner size allowlist — loaded from the active app profile at runtime.
// null means "accept any size" (generic profile default).
// Use getProfileAllowedSizes() to read this safely.
function getProfileAllowedSizes() {
    const profile = getActiveProfile();
    if (!profile.allowedBannerSizes) return null; // no restriction
    return new Set(profile.allowedBannerSizes.map(s => s.toLowerCase()));
}
const fmtSize = (w, h) => `${Number(w)}x${Number(h)}`;

function ortbSchemaChecks(req) {
    const g = mkGroup("ORTB 2.6 (schema + types)");
    // Try requestBodyParsed as fallback (proxy may pre-parse gzipped/binary bodies)
    const bodySource = req.requestBody || req.requestBodyParsed;
    const rq = safeJson(bodySource);
    if (!rq.ok) {
        // Downgrade to warn — proxy capture issues or gzip can cause empty body;
        // this does not mean the live request failed. Distinguish a body that was
        // captured-then-trimmed for memory from one the proxy never got.
        const bs = bodyState(req, 'request');
        add(g, "ortb.req.json", "Request JSON", "warn",
            rq.err && rq.err !== "empty" ? `Not JSON: ${rq.err}` : bs.message);
        return g;
    }
    const b = rq.value || {};

    let typeIssues = 0;
    const tCheck = (ok, id, label, msgBad, sev = "fail") => { if (!ok) { typeIssues++; add(g, id, label, sev, msgBad); } };

    // presence/types
    tCheck(isNonEmptyStr(b.id), "ortb.id", "bidrequest.id type", "Missing/invalid (string required)");

    if (!isObj(b.app)) {
        add(g, "app.missing", "app object", "fail", "Missing");
    } else {
        tCheck(isNonEmptyStr(b.app.bundle), "app.bundle.type", "app.bundle type", "Missing or non-string");
        tCheck(isNonEmptyStr(b.app.domain), "app.domain.type", "app.domain type", "Missing or non-string", "warn");
        tCheck(isNonEmptyStr(b?.app?.publisher?.id), "app.publisher.id.type", "app.publisher.id type", "Missing or non-string");
        tCheck(isNonEmptyStr(b?.app?.content?.url), "app.content.url.type", "app.content.url type", "Missing or non-string", "warn");

        const pbSrc = b?.app?.ext?.prebid?.source;
        const pbVer = b?.app?.ext?.prebid?.version;
        if (pbSrc != null) tCheck(isNonEmptyStr(pbSrc), "app.ext.prebid.source.type", "app.ext.prebid.source type", "Not string", "warn");
        if (pbVer != null) tCheck(isNonEmptyStr(pbVer), "app.ext.prebid.version.type", "app.ext.prebid.version type", "Not string", "warn");
    }

    if (!isObj(b.device)) {
        add(g, "device.missing", "device object", "fail", "Missing");
    } else {
        if (b.device.ifa != null) tCheck(isNonEmptyStr(b.device.ifa), "device.ifa.type", "device.ifa type", "Not string", "warn");
        if (b.device.w != null) tCheck(isNum(b.device.w), "device.w.type", "device.w type", "Not number", "warn");
        if (b.device.h != null) tCheck(isNum(b.device.h), "device.h.type", "device.h type", "Not number", "warn");
        if (b.device.pxratio != null) tCheck(isNum(b.device.pxratio), "device.pxratio.type", "device.pxratio type", "Not number", "warn");
        if (b.device.connectiontype != null) tCheck(isInt(b.device.connectiontype), "device.connectiontype.type", "device.connectiontype type", "Not integer", "warn");
    }

    if (!isArr(b.imp) || b.imp.length === 0) {
        add(g, "ortb.imp", "imp[]", "fail", "Missing/empty (array required)");
    } else {
        b.imp.forEach((imp, idx) => {
            const tag = (x) => `imp[${idx}].${x}`;
            if (!isObj(imp)) { add(g, tag("shape"), `${tag("shape")}`, "fail", "Not an object"); return; }
            const hasAnyType = !!(imp.banner || imp.video || imp.native);
            if (!hasAnyType) add(g, tag("kind"), `${tag("banner|video|native")}`, "fail", "Missing all");

            if (imp.id != null) tCheck(isNonEmptyStr(imp.id), tag("id.type"), `${tag("id type")}`, "Not non-empty string", "warn");

            if (imp.banner) {
                const fmts = imp.banner.format;
                if (!isArr(fmts) || fmts.length === 0) {
                    add(g, tag("banner.format"), `${tag("banner.format")}`, "fail", "Missing/empty (array required)");
                } else {
                    const okStruct = isArrOf(fmts, (f) => isObj(f) && isNum(f.w) && isNum(f.h));
                    if (!okStruct) {
                        add(g, tag("banner.format.type"), `${tag("banner.format type")}`, "fail", "Expect array<{w:number,h:number}>");
                    } else {
                        const sizes = fmts.map(f => fmtSize(f.w, f.h));
                        const allowedSet = getProfileAllowedSizes();
                        if (allowedSet !== null) {
                            // Profile has a size restriction — validate against it
                            const invalid = sizes.filter(s => !allowedSet.has(s.toLowerCase()));
                            if (invalid.length > 0) {
                                add(g, tag("banner.allowedSizes"), "Banner size not in profile allowlist", "warn",
                                    `Unexpected: ${invalid.join(", ")} — update profile if intentional`);
                            }
                        }
                        // Always pass if no profile restriction (generic mode)
                    }
                }
                if (imp.banner.pos != null) tCheck(isInt(imp.banner.pos), tag("banner.pos.type"), `${tag("banner.pos type")}`, "Not integer", "warn");
            }

            if (imp.secure != null) tCheck(isInt(imp.secure), tag("secure.type"), `${tag("secure type")}`, "Not integer");
            if (imp.clickbrowser != null) tCheck(isInt(imp.clickbrowser), tag("clickbrowser.type"), `${tag("clickbrowser type")}`, "Not integer", "warn");

            const gpid = imp?.ext?.gpid;
            if (gpid != null) tCheck(isNonEmptyStr(gpid), tag("ext.gpid.type"), `${tag("ext.gpid type")}`, "Not string");
            else add(g, tag("ext.gpid.missing"), `${tag("ext.gpid")}`, "info", "Missing — GPID adoption is bidder-dependent");

            if (isNonEmptyStr(gpid) && /(^|\/)adh(\/|$)/.test(gpid)) {
                const pos = toNum(imp?.banner?.pos, NaN);
                if (pos !== 1) add(g, tag("banner.pos.adh"), "If GPID contains /adh → pos=1", "fail",
                    Number.isFinite(pos) ? `pos=${pos}` : "pos missing/invalid");
            }

            const impSrId = imp?.ext?.prebid?.storedrequest?.id;
            if (impSrId != null) tCheck(isNonEmptyStr(impSrId), tag("prebid.srid.type"),
                `${tag("ext.prebid.storedrequest.id type")}`, "Not string");
        });
    }

    const topSrId = b?.ext?.prebid?.storedrequest?.id;
    if (topSrId != null) tCheck(isNonEmptyStr(topSrId), "ext.prebid.storedrequest.type",
        "ext.prebid.storedrequest.id type", "Not string");

    const ccpa = b?.regs?.ext?.us_privacy;
    if (ccpa != null) {
        if (!isStr(ccpa)) add(g, "regs.ccpa.type", "regs.ext.us_privacy type", "fail", "not string");
        else if (!isValidCCPA(ccpa)) add(g, "regs.ccpa.shape", "regs.ext.us_privacy shape", "warn", "invalid CCPA format");
    }

    const eids = b?.user?.ext?.eids;
    if (eids != null) {
        if (!isArr(eids)) {
            add(g, "user.eids.type", "user.ext.eids type", "fail", "not array");
        } else {
            const eachOk = eids.every((e) =>
                isNonEmptyStr(e?.source) &&
                isArr(e?.uids) &&
                e.uids.every((u) => (u.id == null || isNonEmptyStr(u.id)) && (u.atype == null || isInt(u.atype)))
            );
            if (!eachOk) add(g, "user.eids.shape", "user.ext.eids shape", "warn", "malformed entries");
        }
    }

    if (typeIssues === 0) add(g, "types.ok", "Type validations", "pass", "OK");

    // ---- app.bundle format (iOS bundle ID or Android package name) ----
    {
        const bundle = b.app?.bundle;
        if (isNonEmptyStr(bundle)) {
            // Valid: com.company.app (Android) or com.Company.AppName (iOS)
            // Invalid: bare words, URLs, empty segments
            const looksValid = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*){1,}$/.test(bundle);
            if (!looksValid) {
                add(g, "app.bundle.format", "app.bundle format", "warn",
                    `'${bundle}' doesn't look like a valid bundle ID (expected com.company.app)`);
            } else {
                add(g, "app.bundle.format.ok", "app.bundle format", "pass", bundle);
            }
        }
    }

    // ---- app.storeurl — required for app-ads.txt verification ----
    if (!b.app?.storeurl) {
        add(g, "app.storeurl", "app.storeurl", "warn",
            "Missing — bidders use storeurl for app-ads.txt verification and brand safety");
    } else {
        add(g, "app.storeurl.ok", "app.storeurl", "pass", String(b.app.storeurl).slice(0, 80));
    }

    // ---- device.geo — geo-targeting signal ----
    if (!b.device?.geo) {
        add(g, "device.geo", "device.geo", "info", "Missing — geo-targeting and country-level frequency capping unavailable");
    } else {
        const geo = b.device.geo;
        const loc = [geo.country, geo.region, geo.city].filter(Boolean).join(" / ") || "present";
        add(g, "device.geo.ok", "device.geo", "pass", loc);
    }

    return g;
}

//////////////////////////// Prebid business/policy + Context compare ////////////////////////////

function prebidChecks(req, ctx = {}) {
    const g = mkGroup("Prebid (policy)");
    // Try requestBodyParsed first (proxy pre-parses gzip/binary); fall back to raw body
    const bodySource = req.requestBody || req.requestBodyParsed;
    const rq = safeJson(bodySource);
    const rs = safeJson(req.responseBody);
    // Downgrade empty body to warn — a successful request with bids proves the body was sent
    // even if the proxy didn't capture it (e.g. gzip, QUIC, proxy restart during capture)
    add(g, "prebid_req_json", "Prebid request JSON", rq.ok ? "pass" : "warn",
        rq.ok ? "OK" : (rq.err && rq.err !== "empty" ? `Not JSON: ${rq.err}` : bodyState(req, 'request').message));
    add(g, "prebid_rsp_json", "Prebid response JSON", rs.ok ? "pass" : "warn", rs.ok ? "OK" : "Not JSON or empty");

    if (!rq.ok) return g;
    const b = rq.value || {};

    const domain = (b?.app?.domain || "").replace(/^www\./, "");
    const contentHost = hostFromUrl(b?.app?.content?.url || "");
    if (domain && contentHost && contentHost !== domain) {
        add(g, "app.domain.mismatch", "app.domain vs content.url", "warn",
            `domain=${domain}, urlHost=${contentHost}`);
    }

    // ext.prebid.cache is needed for Prebid Server caching — only warn if the response
    // actually has bids (if there are no bids, cache being absent is expected and fine)
    const cache = b?.ext?.prebid?.cache;
    const hasCache = cache && (cache.bids || cache.vastxml);
    if (!hasCache && rs.ok) {
        const hasBidsInResp = Array.isArray(rs.value?.seatbid) &&
            rs.value.seatbid.some(s => Array.isArray(s.bid) && s.bid.length > 0);
        if (hasBidsInResp) {
            add(g, "ext.prebid.cache", "ext.prebid.cache", "warn", "Missing bids/vastxml — bids present but no cache config");
        }
        // else: no bids → cache absence is expected, skip
    }

    if (rs.ok) {
        const seatbid = rs.value?.seatbid;
        const hasBid =
            Array.isArray(seatbid) &&
            seatbid.some((s) => Array.isArray(s.bid) && s.bid.some((bb) => (toNum(bb.price) || 0) > 0));
        if (!hasBid) add(g, "prebid_has_bid", "Any bids > 0", "warn", "No bids (or no-bid)");

        // Latency checks (if available in ext)
        const latency = req.validData?.latency || [];
        latency.forEach(l => {
            const ms = Number(l.ms || 0);
            if (ms > 2000) {
                add(g, `prebid_latency_${l.seat}`, `Latency (${l.seat})`, "warn", `Slow: ${ms}ms (>2000ms)`);
            }
        });

        // Schema strictness: Winner must have CRID
        const winner = req.winner;
        if (winner) {
            if (!winner.crid) {
                add(g, "prebid.winner.crid", "Winning bid CRID", "fail", "Missing CRID on winning bid");
            }
            if (!winner.adomain || winner.adomain.length === 0) {
                add(g, "prebid.winner.adomain", "Winning bid adomain", "warn", "Missing adomain on winning bid");
            }
        }
    }

    // --- Recommendations / Best Practices ---
    // 1. GPID check (scan all imps)
    // NOTE: items prefixed "Recommendation:" are best-practice advisories, not
    // failures. They are emitted at 'info' severity so they don't inflate the
    // Warn count or flip the request to a warning state when absent.
    const imps = b.imp || [];
    const missingGpid = imps.filter(i => !i.ext?.gpid && !i.ext?.data?.pbadsl?.gpid);
    if (missingGpid.length > 0) {
        add(g, "rec.gpid", "Recommendation: GPID", "info", `Missing GPID on ${missingGpid.length} imp(s) — improves reporting & brand safety`);
    } else if (imps.length > 0) {
        add(g, "rec.gpid", "Recommendation: GPID", "pass", "GPID present on all imps");
    }

    // 2. Transaction ID (TID)
    if (!b.source?.tid) {
        add(g, "rec.tid", "Recommendation: TID", "info", "Missing source.tid (Transaction ID) — aids auction de-duplication");
    } else {
        add(g, "rec.tid", "Recommendation: TID", "pass", "Transaction ID present");
    }

    // 3. Supply Chain (Schain)
    if (!b.source?.ext?.schain) {
        add(g, "rec.schain", "Recommendation: Schain", "info", "Missing Supply Chain (source.ext.schain) — buyers may discount unverified supply");
    } else {
        add(g, "rec.schain", "Recommendation: Schain", "pass", "Schain present");
    }

    // 4. Consent — CCPA and TCF/GPP, as two independent checks (not one combined
    // verdict). Severity source of truth: proxy-server/prebid/inspectPrebid.js
    // (~lines 52-63) emits these as two separate pushes (`ccpa`/`ccpa.missing` and
    // `tcf`/`gpp`/`tcf.missing`), each defaulting to 'info' — the app-agnostic
    // default, since consent posture varies by region and this validator has no
    // idea which region the traffic is from. Server only raises it to 'warn' when
    // an App Pack's policy.requiresConsent === 'always'; that gate lives server-side
    // (this file has no App Pack policy input), so the frontend check simply mirrors
    // the ungated 'info' default. Mirrors the app-agnostic-detection /
    // policy-raises-it split just shipped in proxy-server/idGraph.js. If the two
    // sides need to diverge again, change inspectPrebid.js first.
    const hasCCPA = !!b.regs?.ext?.us_privacy;
    if (hasCCPA) {
        add(g, "rec.consent.ccpa", "Recommendation: CCPA Consent", "pass", "US Privacy (CCPA) string present");
    } else {
        add(g, "rec.consent.ccpa", "Recommendation: CCPA Consent", "info", "US Privacy (CCPA) string missing");
    }

    const hasTCF = !!b.user?.ext?.consent;
    const hasGPP = !!(b.regs?.gpp || b.regs?.ext?.gpp);
    if (hasTCF) {
        add(g, "rec.consent.tcf", "Recommendation: TCF/GPP Consent", "pass", "TCF Consent string present");
    } else if (hasGPP) {
        add(g, "rec.consent.tcf", "Recommendation: TCF/GPP Consent", "pass", "GPP Consent string present");
    } else {
        add(g, "rec.consent.tcf", "Recommendation: TCF/GPP Consent", "info", "TCF/GPP Consent string missing");
    }

    // 5. Device LMT — only warn if ATT status is absent (ATT supersedes LMT on iOS 14+)
    // If device.ext.atts or device.att is set, the app is using ATT and LMT is redundant
    const hasATT = b.device?.ext?.atts != null || b.device?.att != null;
    if (b.device?.lmt == null && !hasATT) {
        add(g, "rec.lmt", "Recommendation: LMT", "info", "Missing device.lmt — consider device.ext.atts (iOS 14+ ATT)");
    } else if (hasATT) {
        add(g, "rec.lmt", "Recommendation: LMT", "pass", `ATT present (device.ext.atts=${b.device?.ext?.atts ?? b.device?.att}) — LMT superseded`);
    }

    // 6. Test Mode
    if (b.test === 1) {
        add(g, "info.test_mode", "Test Mode", "warn", "Test mode active (test=1)");
    }

    // 7. tmax range (code-level, more precise than KB presence check)
    {
        const tmax = b.tmax;
        if (tmax != null) {
            if (tmax < 500) {
                add(g, "tmax.low", "Bid timeout (tmax)", "warn", `${tmax}ms — very low, bidders will likely time out (recommend ≥1000ms)`);
            } else if (tmax > 8000) {
                add(g, "tmax.high", "Bid timeout (tmax)", "warn", `${tmax}ms — very high, may degrade UX (recommend ≤5000ms)`);
            } else {
                add(g, "tmax.ok", "Bid timeout (tmax)", "pass", `${tmax}ms`);
            }
        }
    }

    // 8. Device IFA — zeroed-out detection + ATT/IFA consistency
    {
        const ZERO_IFA = "00000000-0000-0000-0000-000000000000";
        const ifa = b.device?.ifa;
        const attVal = b.device?.ext?.atts ?? b.device?.att ?? null;
        const ATT_LBL = { 0: "Not Determined", 1: "Restricted", 2: "Denied", 3: "Authorized" };
        if (ifa === ZERO_IFA) {
            add(g, "ifa.zeroed", "Device IFA", "warn",
                "All-zeros IFA (ATT denied/restricted) — bidder targeting is reduced; do not use zero value for frequency capping");
        } else if (ifa) {
            add(g, "ifa.present", "Device IFA", "pass", `${String(ifa).slice(0, 8)}… present`);
        }
        if (attVal != null) {
            add(g, "att.status", "ATT status (device.ext.atts)", attVal === 3 ? "pass" : "info",
                `ATT=${attVal} — ${ATT_LBL[attVal] || "Unknown"}`);
        }
    }

    // 9. All-bidders no-bid detection (from validData.tables.bids)
    {
        const bidRows = req.validData?.tables?.bids || [];
        if (bidRows.length > 0) {
            const activeBids = bidRows.filter(b => b.status === "bid" || b.status === "won");
            if (activeBids.length === 0) {
                add(g, "all.nobid", "All bidders no-bid", "warn",
                    `All ${bidRows.length} bidder(s) returned no bid or timed out — check floor prices, targeting, and bidder config`);
            } else {
                add(g, "bid.summary", "Bid summary", "pass",
                    `${activeBids.length}/${bidRows.length} bidders returned bids`);
            }
        }
    }

    // ---- Config gating for context compare ----
    const { prebid: prebidEnabled } = flags(ctx);
    if (!prebidEnabled) {
        addSkip(g, "prebid.ctx.skipped", "Context vs ORTB (tags.prebid)", "context_api.ad_partners.prebid = false");
        return g;
    }

    // ---- Context compare table (expected tags.prebid or tags.dv.prebid vs ORTB) ----
    const ctxRef = req.contextRef;
    const ctxPB = ctxRef?.payload?.data?.results?.[0]?.tags?.prebid
        || ctxRef?.payload?.data?.results?.[0]?.tags?.dv?.prebid
        || null;

    if (ctxPB && typeof ctxPB === "object") {
        const columns = ["Key", "Expected", "Found (ORTB)", "Status"];
        const rows = [];
        for (const [k, expectedVal] of Object.entries(ctxPB)) {
            const foundVal = findValueByKey(b, k);
            let status = "OK";
            if (Array.isArray(expectedVal) || String(expectedVal).includes(",")) {
                const { ok } = cmpLists(expectedVal, foundVal);
                status = fmtStatus(ok);
            } else {
                status = (String(foundVal ?? "") === String(expectedVal)) ? "OK" : "Mismatch";
            }
            rows.push([k,
                isObj(expectedVal) ? JSON.stringify(expectedVal) : String(expectedVal ?? "—"),
                isObj(foundVal) ? JSON.stringify(foundVal) : String(foundVal ?? "—"),
                status]);
        }
        pushTableRow(g, {
            id: "prebid.ctx.compare",
            label: "Context vs ORTB (tags.prebid)",
            severity: rows.every(r => r[3] === "OK") ? "pass" : "fail",
            columns, rows
        });
    }

    return g;
}

//////////////////////////// GAM ////////////////////////////

function gampadChecks(req, ctx = {}) {
    const g = mkGroup("GAM / gampad");
    const params = qs(req.url);

    // ---- Query param schema/type validation (compact) ----
    const spec = {
        submodel: { label: "Device submodel", check: isNonEmptyStr },
        adid_p: { label: "Ad ID present flag", check: (v) => isEnum(v, ["0", "1"]) },
        format: { label: "Primary size token", check: (v) => typeof v === "string" && /^[0-9]+x[0-9]+_as$/.test(v) },
        ini_pn: { label: "Installer package", check: isPkg },
        ins_pn: { label: "Installer package", check: isPkg },
        omid_v: { label: "OMID version", check: isVersionTag },
        dv: { label: "SDK/device code", check: isIntStr },
        ev: { label: "Env/SDK version", check: isSemverish },
        gl: { label: "Country (ISO-2)", check: isCountry2 },
        hl: { label: "Lang (ISO-2)", check: isLang2 },
        js: { label: "GMA SDK tag", check: isVersionTag },
        lv: { label: "Library version", check: isIntStr },
        mv: { label: "Market source", check: isVersionTag },
        sz: { label: "Allowed sizes", check: isSizesList },
        aos: { label: "Android SDK int", check: isIntStr },
        lft: { label: "LFT flag", check: isBoolStr },
        vnm: { label: "App version name", check: isSemverish },
        ppid: { label: "Publisher Provided ID", check: isUUIDish },
        u_sd: { label: "Device pixel ratio", check: isNumStr },
        request_id: { label: "Request id", check: isIntStr },
        rafmt: { label: "Ad format code", check: isIntStr },
        target_api: { label: "Target SDK", check: isIntStr },
        // carrier can be MCC-MNC digits (e.g. "310260") OR a carrier name string (e.g. "Verizon") — both valid
        carrier: { label: "Carrier / MCCMNC", check: (v) => typeof v === "string" && v.length > 0 },
        seq_num: { label: "Sequence #", check: isIntStr },
        eid: { label: "Experiment IDs", check: (v) => String(v).split(",").every(isIntStr) },
        us_privacy: { label: "US Privacy (CCPA)", check: isCCPA },
        u_w: { label: "Screen width (css px)", check: isIntStr },
        u_h: { label: "Screen height (css px)", check: isIntStr },
        msid: { label: "App package", check: isPkg },
        an: { label: "App build tag", check: isVersionTag },
        u_audio: { label: "Audio state enum", check: isIntStr },
        net: { label: "Network", check: isNet },
        u_so: { label: "Orientation", check: isOrientation },
        output: { label: "Response type", check: (v) => isEnum(v, ["html", "xml", "json"]) },
        region: { label: "Region context", check: (v) => isEnum(v, ["mobile_app", "web"]) },
        content_url: { label: "Content URL", check: isUrl },
        url: { label: "App-scoped host", check: isHostish },
        gdfp_req: { label: "GAM request flag", check: (v) => isEnum(v, ["1"]) },
        m_ast: { label: "Ad stack", check: (v) => isEnum(v, ["afmajs"]) },
        // "ss" = server-side (DAI/stitched). Observed in the DAI captures under
        // conformance/fixtures; the original ifr|s enum false-flagged all of it.
        impl: { label: "Render mode", check: (v) => isEnum(v, ["ifr", "s", "ss"]) },
        iu: { label: "Ad unit path", check: (v) => typeof v === "string" && v.startsWith("/") },
        correlator: { label: "Correlator", check: (v) => /^\d{5,}$/.test(String(v)) },
    };
    const specRows = [];
    Object.keys(spec).forEach((k) => {
        const { label, check, expected } = spec[k];
        const val = params[k];
        if (val == null || val === "") return; // skip completely absent params
        const ok = check(val);
        const status = ok ? "🟢 OK" : "🔴 Mismatch";
        const found = ok ? String(val) : `${String(val)}  (expected: ${expected || "—"})`;
        const note = ok ? "—" : `ⓘ Expected ${expected || "—"}`;
        // Keep columns order: Param | Meaning | Found | Status | Note
        specRows.push([k, label, found, status, note]);
    });
    // Split specRows into FAIL and PASS tables
    const cols = ["Param", "Meaning", "Found", "Status", "Note"];
    const failRows = specRows.filter(r => !String(r[3]).includes("OK"));
    const passRows = specRows.filter(r => String(r[3]).includes("OK"));
    // Summary row above tables
    const total = specRows.length;
    const passCount = passRows.length;
    const failCount = failRows.length;
    // Add counts to group title
    g.title = `GAM / gampad — ${passCount}/${total} OK, ${failCount} fail`;
    pushTableRow(g, {
        id: "gma.params.shape.summary",
        label: "GAM Query Params — Summary",
        severity: failCount > 0 ? "fail" : "pass",
        columns: ["Params present", "Pass", "Fail"],
        rows: [[String(total), String(passCount), String(failCount)]]
    });
    if (failRows.length) {
        pushTableRow(g, {
            id: "gma.params.shape.fail",
            label: `GAM Query Params (type/value) — FAIL (${failCount})`,
            severity: "fail",
            columns: cols,
            rows: failRows
        });
    }
    if (passRows.length) {
        pushTableRow(g, {
            id: "gma.params.shape.pass",
            label: `GAM Query Params (type/value) — PASS (${passCount}/${total})`,
            severity: "pass",
            columns: cols,
            rows: passRows,
            collapsed: true
        });
    }

    // Parse cust_params once (used by DV/Mobian)
    const cpRaw = params.cust_params || params.custParams || "";
    const cpDec = decodePlusURIComponent(cpRaw);
    const cpObj = parseKvString(cpDec);

    // helper to detect article-like URLs (/section/slug)
    const isArticleUrl = (u) => {
        try {
            const { pathname } = new URL(u);
            const segs = (pathname || '').split('/').filter(Boolean);
            return segs.length >= 2;
        } catch { return false; }
    };
    const iu = params.iu || params.ad_unit || "";
    const contentUrl = params.content_url || params.url || "";
    const looksArticle = /(^|\/)art(\/|$)/i.test(iu) || isArticleUrl(contentUrl);

    if (!iu) {
        add(g, "dfp_iu", "iu (ad unit) present", "fail", "Missing iu param");
    } else {
        add(g, "dfp_iu_ok", "iu (ad unit) present", "pass", iu);
    }

    const sz = params.sz || "";
    if (!sz) {
        add(g, "dfp_sz", "sz (size) present", "warn", "Missing sz size");
    } else {
        // Accept multiple sizes separated by '|' or ','
        const tokens = sz.split(/[|,]/).map(s => s.trim()).filter(Boolean);
        const bad = tokens.filter(s => !/^[0-9]+x[0-9]+$/.test(s));
        if (bad.length) {
            add(g, "dfp_sz_shape", "sz (size) format", "fail", `Invalid: ${bad.join('|')} · Given: ${sz}`);
        } else {
            add(g, "dfp_sz_ok", "sz (size) format", "pass", `Sizes OK (${sz})`);
        }
    }

    const output = params.output || "";
    if (!output) {
        add(g, "dfp_output", "output parameter", "info", "Not set");
    } else {
        add(g, "dfp_output_ok", "output parameter", "pass", output);
    }

    const bodyText = asText(req.responseBody);
    if (looksBinaryString(bodyText)) {
        add(g, "dfp_rsp_shape", "Response looks like GAM", "warn", "Binary/opaque response");
    } else {
        const rsp = bodyText.toLowerCase();
        const looksLikeDFP = includes(rsp, '"adserver":"dfp"') || includes(rsp, "google");
        if (!looksLikeDFP) add(g, "dfp_rsp_shape", "Response looks like GAM", "warn", "Could not confirm");
    }

    // --- Attach context (if server provided it) ---
    const ctxRef = req.contextRef;
    if (ctxRef && ctxRef.payload) {
        const key = ctxRef.key || {};
        const ageMs = (() => {
            const t = Date.parse(ctxRef.cachedAt || "");
            return Number.isFinite(t) ? Date.now() - t : NaN;
        })();
        const ageText = Number.isFinite(ageMs) ? `${Math.round(ageMs / 1000)}s old` : "age unknown";

        add(
            g,
            "ctx.linked",
            "Context (latest) attached",
            "pass",
            `key: url=${key.url || "(none)"} article_id=${key.article_id || "-"} video_id=${key.video_id || "-"} · ${ageText}`
        );
    } else {
        add(
            g,
            "ctx.missing",
            "Context (latest) attached",
            "warn",
            "No latest context cached or no match for this content_url"
        );
    }

    // Article-page requirements: require c & url in cust_params on article-like requests
    if (looksArticle) {
        if (cpObj.c) {
            add(g, "cust_params.c.ok", "cust_params.c", "pass", "Found category list (c)");
        } else {
            add(g, "cust_params.c.missing", "cust_params.c", "fail", "Missing for article content");
        }
        if (cpObj.url) {
            add(g, "cust_params.url.ok", "cust_params.url", "pass", String(cpObj.url).slice(0, 200));
        } else {
            add(g, "cust_params.url.missing", "cust_params.url", "fail", "Missing for article content");
        }
    }

    // ---- ATT status (iOS) ----
    {
        const attmasVal = params.attmas;
        if (attmasVal != null && String(attmasVal).trim() !== "") {
            // attmas arrives in two shapes depending on the SDK/app: the GMA numeric
            // code (0-3) or a humanised word ("authorized" | "denied" | ...). Normalise
            // both to a canonical status so a valid Authorized state never reads as an
            // "Unknown" warn. (InspectraNetworkView renders the word form; this keeps
            // the two views consistent.)
            const raw = String(attmasVal).trim().toLowerCase();
            const ATT_STATUS = {
                "0": "notDetermined", "1": "restricted", "2": "denied", "3": "authorized",
                notdetermined: "notDetermined", "not_determined": "notDetermined", "not-determined": "notDetermined",
                restricted: "restricted", denied: "denied", authorized: "authorized",
            };
            const ATT_LABELS = {
                notDetermined: "Not Determined", restricted: "Restricted",
                denied: "Denied", authorized: "Authorized",
            };
            const status = ATT_STATUS[raw] || null;
            const isValidAtt = status != null;
            const attLabel = isValidAtt ? ATT_LABELS[status] : `Unknown (${attmasVal})`;
            // Not-Determined/Restricted/Denied/Authorized are all legitimate user
            // choices — only an unrecognised attmas value is actually a problem.
            add(g, "attmas", "ATT Status (attmas)", isValidAtt ? "info" : "warn",
                `ATT=${attmasVal} — ${attLabel}`);
            // npa consistency: denied/restricted requires npa=1 (kept at its current severity)
            if ((status === "restricted" || status === "denied") && params.npa !== "1") {
                add(g, "attmas.npa", "NPA when ATT denied/restricted", "fail",
                    `ATT is ${attLabel} but npa=${params.npa != null ? `'${params.npa}'` : "absent"} — must be npa=1 to block personalised ads`);
            }
        }
    }

    // ---- hb_env value ----
    {
        const hbEnv = cpObj.hb_env || req.validData?.expanded?.hbEnv;
        if (hbEnv) {
            if (hbEnv === "mobile-app") {
                add(g, "hb_env.ok", "Prebid hb_env", "pass", "mobile-app ✓");
            } else {
                add(g, "hb_env.bad", "Prebid hb_env", "warn",
                    `'${hbEnv}' — expected 'mobile-app' for Prebid Mobile SDK`);
            }
        }
    }

    // ---- hb_pb price bucket validation ----
    {
        const hbPbRaw = cpObj.hb_pb;
        if (hbPbRaw) {
            const pbNum = parseFloat(hbPbRaw);
            if (!isNaN(pbNum)) {
                const GRANULARITIES = {
                    low:    [{ max: 5.00,  inc: 0.50 }],
                    medium: [{ max: 20.00, inc: 0.10 }],
                    auto:   [{ max: 5.00,  inc: 0.05 }, { max: 10.00, inc: 0.10 }, { max: 20.00, inc: 0.50 }],
                    dense:  [{ max: 3.00,  inc: 0.01 }, { max: 8.00,  inc: 0.05 }, { max: 20.00, inc: 0.50 }],
                    high:   [{ max: 20.00, inc: 0.01 }],
                };
                const onBucket = (price, buckets) => {
                    for (const b of buckets) {
                        if (price <= b.max) {
                            const rem = price % b.inc;
                            return rem < 0.001 || b.inc - rem < 0.001;
                        }
                    }
                    return false;
                };
                let matchedGran = null;
                for (const [name, buckets] of Object.entries(GRANULARITIES)) {
                    if (onBucket(pbNum, buckets)) { matchedGran = name; break; }
                }
                if (matchedGran) {
                    add(g, "hb_pb.bucket", "Prebid price bucket (hb_pb)", "pass",
                        `$${hbPbRaw} — '${matchedGran}' granularity`);
                } else {
                    // Not matching a stock preset isn't a fault — custom price buckets
                    // are a normal, deliberate Prebid config. Advisory only.
                    add(g, "hb_pb.bucket", "Prebid price bucket (hb_pb)", "info",
                        `$${hbPbRaw} doesn't align to any standard granularity (low/medium/auto/dense/high) — fine if custom buckets are configured`);
                }
            }
        }
    }

    // ---- GAM response: demand source, CPM, slot size, refresh ----
    {
        const expanded    = req.validData?.expanded || {};
        const winnerCpm   = expanded.winnerCpm;
        const floorCpm    = expanded.floorCpm;
        const creativeSource = expanded.creativeSource;
        const noFill      = req.validData?.noFill;
        const isRefresh   = expanded.isRefresh;
        const gamSlotSizes = expanded.gamSlotSizes;

        // Fill / no-fill
        if (noFill) {
            add(g, "gam.fill", "GAM fill status", "warn", "No fill — GAM returned empty response");
        } else if (winnerCpm != null) {
            add(g, "gam.winner_cpm", "Winning CPM", "pass", `$${Number(winnerCpm).toFixed(4)}`);
        }

        // CPM vs Prebid floor
        if (!noFill && winnerCpm != null && floorCpm != null) {
            const ok = winnerCpm >= floorCpm;
            add(g, "gam.cpm_floor", "CPM ≥ floor (hb_pb)", ok ? "pass" : "warn",
                ok ? `$${winnerCpm} ≥ floor $${floorCpm}`
                   : `Winner $${winnerCpm} < floor $${floorCpm} — price bucket may not match line item`);
        }

        // Demand source
        if (creativeSource?.label) {
            add(g, "gam.creative_source", "Demand source", creativeSource.confirmed ? "pass" : "warn",
                creativeSource.label);
        }

        // Refresh
        if (isRefresh) {
            const rc = expanded.refreshCount;
            add(g, "gam.refresh", "Ad refresh", "pass",
                rc != null ? `Refresh #${rc}` : "Refresh detected (prev_scp present)");
        }

        // GAM resolved slot size vs requested sz
        if (gamSlotSizes && gamSlotSizes.length > 0) {
            const szParam = params.sz || "";
            const requested = new Set(szParam.split(/[|,]/).map(s => s.trim().toLowerCase()).filter(Boolean));
            const resolved  = gamSlotSizes.map(s => String(s).toLowerCase());
            const unexpected = resolved.filter(s => !requested.has(s));
            if (unexpected.length > 0) {
                add(g, "gam.slot_size", "GAM resolved slot size", "warn",
                    `Resolved [${resolved.join(", ")}] not in requested sz (${szParam}) — size mismatch`);
            } else {
                add(g, "gam.slot_size", "GAM resolved slot size", "pass",
                    `Resolved: ${resolved.join(", ")}`);
            }
        }
    }

    // --- Config gating for GAM compares ---
    const { gma: gmaEnabled } = flags(ctx);
    if (!gmaEnabled) {
        addSkip(g, "gma.dv.skipped", "DV g-list (expected vs GAM cust_params.g)", "context_api.ad_partners.gma = false");
        addSkip(g, "gma.mobian.skipped", "Mobian (expected tags.mobian.gam.cust_params vs GAM)", "context_api.ad_partners.gma = false");
        return g;
    }

    // ---------- DV "g" list: table compare ----------
    const dvchk = req?.validData?.dvCheck;
    const ctxDV = ctxRef?.payload?.data?.results?.[0]?.tags?.dv?.gam?.cust_params || "";
    const gamG = cpObj.g || "";

    const dvCompare = dvchk
        ? { missing: dvchk.missing || [], extra: dvchk.extra || [], ok: !!dvchk.passed }
        : cmpLists(ctxDV, gamG);

    pushTableRow(g, {
        id: "gma.dv.table",
        label: "DV g-list (expected vs GAM cust_params.g)",
        severity: dvCompare.ok ? "pass" : "fail",
        columns: ["Param", "Expected", "Found", "Missing", "Extra", "Status"],
        rows: [
            ["g",
                String(ctxDV || "—"),
                String(gamG || "—"),
                dvCompare.missing.length ? dvCompare.missing.join(", ") : "—",
                dvCompare.extra.length ? dvCompare.extra.join(", ") : "—",
                fmtStatus(dvCompare.ok)]
        ]
    });

    // ---------- Mobian key-values: table compare ----------
    const ctxMobian = ctxRef?.payload?.data?.results?.[0]?.tags?.mobian?.gam?.cust_params
        || ctxRef?.payload?.data?.results?.[0]?.tags?.mobian?.gam
        || null;

    if (ctxMobian && typeof ctxMobian === "object") {
        const mobianKeys = Object.keys(ctxMobian);
        const columns = ["Key", "Expected", "Found", "MissingTokens", "ExtraTokens", "Status"];
        const rows = [];

        mobianKeys.forEach((k) => {
            const expected = ctxMobian[k];
            const found = cpObj[k] ?? cpObj[`mobian_${k}`]; // tolerate both forms
            const { missing, extra, ok } = cmpLists(expected, found);
            rows.push([k,
                isObj(expected) ? JSON.stringify(expected) : String(expected ?? "—"),
                isObj(found) ? JSON.stringify(found) : String(found ?? "—"),
                missing.length ? missing.join(", ") : "—",
                extra.length ? extra.join(", ") : "—",
                fmtStatus(ok)
            ]);
        });

        pushTableRow(g, {
            id: "gma.mobian.table",
            label: "Mobian (expected tags.mobian.gam.cust_params vs GAM cust_params)",
            severity: rows.every(r => r[5] === "OK") ? "pass" : "fail",
            columns, rows
        });
    }

    return g;
}

//////////////////////////// cust_params Rules ////////////////////////////

/**
 * custParamsRulesCheck — validates cust_params against the active profile's custParamRules.
 *
 * Each rule in custParamRules:
 *   {
 *     key:      string          // cust_params key to check (required)
 *     label:    string          // human-readable name (optional, falls back to key)
 *     required: boolean         // fail/warn if key is missing (default: true)
 *     values:   string[]        // allowed values enum (optional — omit = any non-empty value)
 *     pattern:  string          // regex pattern string for value validation (optional)
 *     pages:    string[]        // only apply to these IU page segments, e.g. ['art','hp']
 *                               // (optional — omit or [] = apply to all pages)
 *     severity: 'fail'|'warn'   // severity when rule fails (default: 'fail')
 *   }
 *
 * Page detection: extracted from the IU param at segment index 3
 *   IU format: /networkId/brand/platform/pageType/section/pos
 *   e.g. /4145/fnc/iosmob/art/politics/ban1  → page = 'art'
 *
 * Works for both GAM requests (cust_params in URL query) and IMA requests.
 *
 * Request-type scoping:
 *   - A rule may declare `requestTypes: ['google', 'ima', ...]` to restrict where
 *     it runs. Omit it (the default) and the rule applies to GAM requests.
 *   - The profile's cust_params rules are authored for GAM display targeting, so
 *     on an IMA / SSAI stream registration we do NOT fail on missing GAM-only keys
 *     (app, app_version, xid, fp_lr, …). Those targeting values live in the GAM DAI
 *     server-side config, not in the client stream registration — flagging them
 *     produces false positives. A rule must explicitly opt in via
 *     `requestTypes: ['ima']` to be enforced on IMA.
 *
 * @param {object} req
 * @param {string} [type] inferred request type (e.g. 'google', 'ima')
 * @param {Array} [rules] cust_params rules; defaults to the active profile's
 *   custParamRules (WP4a seam — pass explicitly to keep this a pure function
 *   of its arguments, matching the server port at
 *   proxy-server/validation/custParamsRules.js)
 */
export function custParamsRulesCheck(req, type, rules = getActiveProfile().custParamRules) {
    // WP4a seam (docs/frontend-validation-port-spec.md §4): `rules` is now a
    // plain argument, matching the portable core ported to
    // proxy-server/validation/custParamsRules.js — same logic, no
    // getActiveProfile() call inside it. This default keeps every existing
    // caller (buildSignOffReport.js calls with just `req`; runValidations()
    // below now passes the profile's rules explicitly) behaving identically.
    const reqType = String(type || req?.requestType || '').toLowerCase();
    const isIma = reqType === 'ima';

    // Skip if no rules configured
    if (!rules || rules.length === 0) return null;

    const g = mkGroup('cust_params Rules');

    // Parse cust_params from URL (works for GAM, IMA GET, etc.)
    const params = qs(req.url);
    const cpRaw = params.cust_params || params.custParams || '';
    const cpDec = decodePlusURIComponent(cpRaw);
    const cpObj = parseKvString(cpDec);

    // Also check IMA POST body (adTagParameters.cust_params)
    let bodyObj = {};
    try {
        const raw = req.requestBody || req.requestBodyParsed;
        const parsed = typeof raw === 'object' ? raw : (raw ? JSON.parse(raw) : null);
        if (parsed) {
            const adTagParams = parsed.adTagParameters || parsed.ad_tag_parameters || {};
            const bodyRaw = parsed.cust_params || adTagParams.cust_params || '';
            if (bodyRaw) {
                const bodyDec = decodePlusURIComponent(bodyRaw);
                bodyObj = parseKvString(bodyDec);
            }
        }
    } catch {}

    // Merge: URL takes priority, body fills gaps
    const cp = { ...bodyObj, ...cpObj };

    // Detect page type — match against either the IU segment OR cust_params pageType.
    // IU format:    /networkId/brand/platform/<page>/section/pos  → segment index 3
    // cust_params:  pageType=special-event | event | story | team | watch | sfvod | athlete
    // A rule's `pages` list may reference either form, so we collect all candidates.
    const iu = params.iu || '';
    let iuPage = null;
    if (iu) {
        try {
            const segs = iu.replace(/^\//, '').split('/').filter(Boolean);
            iuPage = segs[3] || null; // e.g. 'world-cup', 'event', 'team'
        } catch {}
    }
    const pageTypeRaw = cp.pageType || cp.pagetype || '';
    const pageCandidates = [iuPage, pageTypeRaw]
        .filter(Boolean)
        .map(s => String(s).toLowerCase());

    // Token matcher — validates a single value, or (when multi) every
    // comma-separated token. ALL tokens must pass; returns the offending ones.
    const checkTokens = (rawVal, { allowed, re, multi }) => {
        const tokens = multi
            ? String(rawVal).split(',').map(t => t.trim()).filter(t => t !== '')
            : [String(rawVal)];
        const bad = [];
        for (const t of tokens) {
            let ok;
            if (allowed)  ok = allowed.includes(t);
            else if (re)  ok = re.test(t);
            else          ok = true;
            if (!ok) bad.push(t);
        }
        return { tokens, bad };
    };

    let anyRuleRan = false;
    let passCount = 0, failCount = 0;

    // Does a rule explicitly opt in to the current request type?
    const explicitTypes = (rule) =>
        Array.isArray(rule.requestTypes)
            ? rule.requestTypes.map(t => String(t).toLowerCase())
            : null;

    rules.forEach((rule, idx) => {
        if (!rule || !rule.key) return;

        // Request-type filter — if a rule scopes to specific request types and the
        // current type isn't one of them, skip it entirely.
        const types = explicitTypes(rule);
        if (types && types.length && !types.includes(reqType)) return;

        // Page filter — skip rule if it scopes to pages and none of the
        // detected page candidates match. If page is unknown, run anyway.
        if (rule.pages && rule.pages.length > 0 && pageCandidates.length) {
            const want = rule.pages.map(p => String(p).toLowerCase());
            if (!want.some(w => pageCandidates.includes(w))) return;
        }

        // On IMA, a rule is only enforced as "required" if it explicitly opts in.
        // Otherwise the rule is GAM-display oriented and missing keys are expected.
        const enforceOnIma = !isIma || (types && types.includes('ima'));

        anyRuleRan = true;
        const ruleLabel = rule.label || rule.key;
        const sev = rule.severity || 'fail';
        const id = `cust_params.rule_${idx}_${rule.key}`;
        const val = cp[rule.key];
        const missing = val === undefined || val === null || val === '';

        if (missing) {
            if (rule.required !== false && enforceOnIma) {
                // Key is required and missing
                const pageScope = rule.pages && rule.pages.length > 0
                    ? ` (pages: ${rule.pages.join('|')})`
                    : '';
                add(g, id, `cust_params.${rule.key}`, sev,
                    `Missing required key "${rule.key}"${pageScope}`);
                failCount++;
            }
            // Optional keys (and GAM-only required keys on IMA) are skipped silently —
            // we do not emit a "pass" row for an absent optional key, which previously
            // flooded the tab with green "Optional — not present" entries.
            return;
        }

        // Key present — validate value if rules given
        const strVal = String(val);

        if (rule.values && rule.values.length > 0) {
            // Enum validation (optionally multi-value: every comma token must be allowed)
            const allowed = rule.values.map(String);
            const { tokens, bad } = checkTokens(strVal, { allowed, multi: rule.multi });
            if (bad.length === 0) {
                add(g, id, `cust_params.${rule.key}`, 'pass',
                    rule.multi && tokens.length > 1
                        ? `all ${tokens.length} values allowed ✓`
                        : `"${strVal}" ✓`);
                passCount++;
            } else {
                add(g, id, `cust_params.${rule.key}`, sev,
                    `unexpected value${bad.length > 1 ? 's' : ''} [${bad.join(', ')}] not in allowed [${allowed.join(', ')}]`);
                failCount++;
            }
        } else if (rule.pattern) {
            // Regex validation (optionally multi-value: every comma token must match)
            let re;
            try { re = new RegExp(rule.pattern); } catch { re = null; }
            if (!re) {
                add(g, id, `cust_params.${rule.key}`, 'warn',
                    `Invalid regex pattern in rule: ${rule.pattern}`);
            } else {
                const { tokens, bad } = checkTokens(strVal, { re, multi: rule.multi });
                if (bad.length === 0) {
                    add(g, id, `cust_params.${rule.key}`, 'pass',
                        rule.multi && tokens.length > 1
                            ? `all ${tokens.length} values match /${rule.pattern}/ ✓`
                            : `"${strVal}" matches /${rule.pattern}/`);
                    passCount++;
                } else {
                    add(g, id, `cust_params.${rule.key}`, sev,
                        `value${bad.length > 1 ? 's' : ''} [${bad.join(', ')}] do not match /${rule.pattern}/`);
                    failCount++;
                }
            }
        } else {
            // No value validation — presence alone is enough
            add(g, id, `cust_params.${rule.key}`, 'pass', `Present: "${strVal.length > 60 ? strVal.slice(0, 60) + '…' : strVal}"`);
            passCount++;
        }
    });

    if (!anyRuleRan) return null;       // all rules were page/type-filtered out
    if (!g.results.length) return null; // nothing worth showing (e.g. all GAM-only keys absent on IMA)

    g.title = `cust_params Rules — ${passCount}/${passCount + failCount} OK`;
    return g;
}

//////////////////////////// Amazon APS ////////////////////////////

function amazonChecks(req, ctx = {}) {
    const g = mkGroup("Amazon APS");
    const rq = safeJson(req.requestBody);
    const rs = safeJson(req.responseBody);
    if (!rs.ok) add(g, "amazon_rsp_json", "Amazon response JSON", "warn", "Not JSON or empty");
    else {
        const hasSlot = !!(rs.value?.ads || rs.value?.ad);
        if (!hasSlot) add(g, "amazon_slots", "Ads object present", "warn", "Not found");
    }

    // ---- Bid outcome (persisted in validData — survives history reload) ----
    {
        const bidOutcome = req.validData?.bidOutcome;
        if (bidOutcome) {
            if (bidOutcome.hasBid) {
                add(g, "aps.bid.result", "APS bid result", "pass",
                    `Bid received — ${bidOutcome.tokenCount} token(s) · ${bidOutcome.slotCount} slot(s) requested${bidOutcome.firstToken ? ` · token: ${bidOutcome.firstToken}` : ""}`);
            } else {
                // No-bid is a normal auction outcome, not a fault — keep it visible but advisory.
                add(g, "aps.no_bid", "APS bid result", "info",
                    `No bid — APS returned no tokens (${bidOutcome.slotCount} slot(s) requested). Normal if no Amazon demand for this placement.`);
            }
        }
    }

    // ---- Request body checks (App ID, SDK, slots, privacy, LMT) ----
    if (rq.ok) {
        const body = rq.value || {};

        // App ID + SDK version
        if (body.appId)  add(g, "aps.app_id",  "Amazon App ID",      "pass", body.appId);
        if (body.adsdk)  add(g, "aps.sdk",      "APS SDK version",    "pass", body.adsdk);
        if (body.pkg?.pn) add(g, "aps.pkg",     "Package name",       "pass", body.pkg.pn);

        // Privacy / CCPA
        const privacyVal = body.privacy || body.us_privacy;
        if (privacyVal) {
            const validFmt = /^1[YNyn-]{3}$/.test(privacyVal);
            add(g, "aps.privacy", "Privacy string (CCPA)", validFmt ? "pass" : "warn",
                validFmt ? `${privacyVal} ✓` : `'${privacyVal}' — expected IAB CCPA format (1YNN)`);
        } else {
            add(g, "aps.privacy.missing", "Privacy string (CCPA)", "warn",
                "Missing — CCPA us_privacy not passed to Amazon");
        }

        // LMT (Limit Ad Tracking) — 0 = allowed, 1 = limited
        const lmtVal = body.dinfo?.lmt;
        if (lmtVal != null) {
            // lmt reflects the user's device privacy choice (ATT) — a state to report, not a fault.
            add(g, "aps.lmt", "Limit Ad Tracking (lmt)", lmtVal === 0 ? "pass" : "info",
                `lmt=${lmtVal} — ${lmtVal === 1 ? "Ad tracking limited (ATT denied/restricted)" : "Tracking allowed"}`);
        }

        // Slot info table — UUID + sizes
        const slots = body.slots || [];
        if (slots.length > 0) {
            const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const columns = ["#", "Slot UUID", "Sizes", "UUID valid"];
            const rows = slots.map((s, i) => {
                const uuid = s.slot || s.slotID || s.id || "—";
                const sizes = Array.isArray(s.sizes)
                    ? s.sizes.map(sz => `${sz.width || sz.w}x${sz.height || sz.h}`).join(", ")
                    : (s.size ? `${s.size.width}x${s.size.height}` : "—");
                const uuidOk = UUID_RE.test(uuid) ? "✓" : "✗ invalid";
                return [String(i + 1), uuid, sizes, uuidOk];
            });
            const hasInvalidUuid = rows.some(r => r[3].startsWith("✗"));
            // Severity source of truth: proxy-server/utils.js validateAmazonRequest()
            // (~line 677, the "Slot UUID format validation" block) emits this as
            // severity:'required' (-> fail via proxyIssuesChecks). A malformed slot
            // UUID means APS demand can't be matched to the slot — a hard failure,
            // not an advisory — so this must fail here too, not warn. If the two
            // sides need to diverge again, change validateAmazonRequest first.
            pushTableRow(g, {
                id: "aps.slots.table",
                label: "APS Slots",
                severity: hasInvalidUuid ? "fail" : "pass",
                columns, rows,
                collapsed: !hasInvalidUuid,
            });
        }
    }

    const { aps: apsEnabled } = flags(ctx);
    if (!apsEnabled) {
        addSkip(g, "aps.ctx.skipped", "Context vs APS request (tags.dv.aps)", "profile has no APS/APS-video and ad_partners.aps_display/aps_video are both false");
        return g;
    }

    // ---- Context compare table (expected tags.dv.aps vs request JSON) ----
    const ctxRef = req.contextRef;
    const ctxAPS = ctxRef?.payload?.data?.results?.[0]?.tags?.dv?.aps || null;
    if (rq.ok && ctxAPS && typeof ctxAPS === "object") {
        const body = rq.value || {};
        const columns = ["Key", "Expected", "Found (APS req)", "Status"];
        const rows = [];

        // flatten expected one level (aps may contain apc/standard/etc.)
        const work = [];
        for (const [k, v] of Object.entries(ctxAPS)) {
            if (v && typeof v === "object" && !Array.isArray(v)) {
                for (const [kk, vv] of Object.entries(v)) work.push([`${k}.${kk}`, vv]);
            } else {
                work.push([k, v]);
            }
        }
        work.forEach(([k, expectedVal]) => {
            const lastKey = k.split(".").pop();
            const foundVal = findValueByKey(body, lastKey);
            let status = "OK";
            if (Array.isArray(expectedVal) || String(expectedVal).includes(",")) {
                const { ok } = cmpLists(expectedVal, foundVal);
                status = fmtStatus(ok);
            } else {
                status = (String(foundVal ?? "") === String(expectedVal)) ? "OK" : "Mismatch";
            }
            rows.push([k,
                isObj(expectedVal) ? JSON.stringify(expectedVal) : String(expectedVal ?? "—"),
                isObj(foundVal) ? JSON.stringify(foundVal) : String(foundVal ?? "—"),
                status
            ]);
        });

        pushTableRow(g, {
            id: "aps.ctx.compare",
            label: "Context vs APS request (tags.dv.aps)",
            severity: rows.every(r => r[3] === "OK") ? "pass" : "fail",
            columns, rows
        });
    }

    return g;
}

//////////////////////////// Amazon APS OTT (amazon_video / OpenRTB C2S) ////////////////////////////

/**
 * amazonVideoChecks — dedicated section for `amazon_video` requests (Amazon APS
 * OTT / server-to-server OpenRTB video bid requests). Surfaces:
 *   - validData.ortb.issues     — generic OpenRTB envelope validator (plain strings,
 *                                 prefixed by category e.g. "OpenRTB: ...", "GPP
 *                                 Warning: ...", "Privacy Error: ...")
 *   - validData.videoOrtb.checks — IAB video-object grading, incl. any A2S privacy
 *                                 checks the proxy appends ({key,label,status,detail})
 * Mirrors the idiom used by prebidChecks/ortbSchemaChecks: one row per finding,
 * mapped through the same severity vocabulary as proxyIssuesChecks. Defensive:
 * absent validData never throws — falls back to a single informational row.
 */
function amazonVideoChecks(req) {
    const g = mkGroup("Amazon APS OTT (OpenRTB video)");
    const ortb = req.validData?.ortb;
    const videoOrtb = req.validData?.videoOrtb;

    if (!ortb && !videoOrtb) {
        add(g, "amazon_video.no_data", "OpenRTB analysis", "info",
            "No OpenRTB analysis available for this request.");
        return g;
    }

    // ---- Envelope (generic OpenRTB) issues — plain strings, category-prefixed ----
    if (ortb) {
        const ortbIssues = Array.isArray(ortb.issues) ? ortb.issues : [];
        if (ortbIssues.length === 0) {
            add(g, "amazon_video.ortb.ok", "OpenRTB envelope", "pass", "No envelope issues");
        } else {
            ortbIssues.forEach((msg, i) => {
                const text = String(msg || "");
                // Category prefix conveys severity: "... Error: ..." → fail,
                // "... Warning: ..." → warn, "missing required ..." → fail,
                // everything else (recommended/advisory) → warn.
                let sev;
                if (/error:/i.test(text) || /missing required/i.test(text)) sev = "fail";
                else if (/warning:/i.test(text)) sev = "warn";
                else if (/recommended/i.test(text)) sev = "info";
                else sev = "warn";
                add(g, `amazon_video.ortb.${i}`, "OpenRTB envelope", sev, text);
            });
        }
        if (ortb.version) add(g, "amazon_video.ortb.version", "OpenRTB version", "info", String(ortb.version));
    }

    // ---- Video-object grading (mimes, protocols, plcmt, api/OMID, pods, A2S privacy…) ----
    if (videoOrtb) {
        const checks = Array.isArray(videoOrtb.checks) ? videoOrtb.checks : [];
        if (!videoOrtb.isVideo) {
            add(g, "amazon_video.not_video", "Video object", "info", "Not a video bid request (no imp[].video)");
        } else if (checks.length === 0) {
            add(g, "amazon_video.checks.none", "Video-object checks", "info", "No video-object findings");
        } else {
            checks.forEach((c, i) => {
                const sev = String(c?.status || "").toLowerCase() || "info";
                add(g, `amazon_video.check.${c?.key || i}`, c?.label || "Video check", sev, c?.detail || "");
            });
        }
    }

    return g;
}

//////////////////////////// Context / Config ////////////////////////////

function contextChecks(req) {
    const g = mkGroup("Context API", "SYSTEM");
    const rs = safeJson(req.responseBody);
    if (!rs.ok) { add(g, "ctx_json", "Context response JSON", "fail", rs.err); return g; }

    // Check DV tag presence only (no config attributes here)
    const dv = rs.value?.data?.results?.[0]?.tags?.dv || {};
    const required = ["gam", "aps", "prebid"];
    const missing = required.filter((k) => !(k in dv));
    if (missing.length === 0) {
        add(g, "ctx_tags", "DV tags present (gam/aps/prebid)", "pass", "OK");
    } else {
        add(g, "ctx_tags_missing", "DV tags present (gam/aps/prebid)", "warn", `Missing: ${missing.join(", ")}`);
    }

    return g;
}

function configurationChecks(req) {
    const g = mkGroup("Configuration", "SYSTEM");
    const rs = safeJson(req.responseBody);
    if (!rs.ok) { add(g, "cfg_json", "Config response JSON", "fail", rs.err); return g; }
    const attrs = rs.value?.data?.attributes || {};
    if (Object.keys(attrs).length === 0) add(g, "cfg_attrs", "Attributes present", "warn", "Empty");

    const ctxEnabled = !!(attrs?.context_api?.enabled);
    add(g, "cfg_ctx_flag", "Context API flag", "pass", ctxEnabled ? "enabled" : "disabled");

    return g;
}

// Tracking (Impression/Click) specific checks
function trackingChecks(req) {
    const g = mkGroup("Tracking Beacon");
    const status = Number(req.status ?? req.responseCode ?? 0);

    // Critical: Clicks must succeed (2xx or 3xx redirect)
    if (req.type === 'click') {
        if (status >= 400) {
            add(g, "click.status", "Click tracker status", "fail", `Click failed with status ${status}`);
        } else {
            add(g, "click.status", "Click tracker status", "pass", `Click OK (${status})`);
        }
    }

    // Impressions should generally succeed
    if (req.type === 'impression') {
        if (status >= 400) {
            add(g, "imp.status", "Impression beacon status", "fail", `Impression failed with status ${status}`);
        } else {
            add(g, "imp.status", "Impression beacon status", "pass", `Impression OK (${status})`);
        }
    }

    return g;
}

//////////////////////////// OMID Validation ////////////////////////////

function omidChecks(req) {
    const g = mkGroup("OMID Verification", "OMID");
    const params = qs(req.url);

    // 1. Common Checks (adSessionId, timestamp)
    if (!params.adSessionId) add(g, "omid.adSessionId", "adSessionId", "fail", "Missing");
    else add(g, "omid.adSessionId", "adSessionId", "pass", "Present");

    if (!params.timestamp) {
        add(g, "omid.timestamp", "timestamp", "fail", "Missing");
    } else {
        // Timestamp is ms since epoch — check it's within the last 24h
        const tsMs = Number(params.timestamp);
        const ageMs = Date.now() - tsMs;
        if (!Number.isFinite(tsMs) || tsMs <= 0) {
            add(g, "omid.timestamp", "timestamp", "warn", `Non-numeric timestamp: ${params.timestamp}`);
        } else if (ageMs > 86_400_000) {
            add(g, "omid.timestamp", "timestamp", "warn", `Timestamp is ${Math.round(ageMs / 3_600_000)}h old — may be a stale or replayed event`);
        } else {
            add(g, "omid.timestamp", "timestamp", "pass", `${Math.round(ageMs / 1000)}s ago`);
        }
    }

    const flowType = params.type;
    if (!flowType) {
        add(g, "omid.type", "Event Type", "fail", "Missing 'type' parameter");
        return g;
    }
    add(g, "omid.type", "Event Type", "info", flowType);

    // Parse 'event' param if present (most complex events have it)
    let evtData = null;
    if (params.event) {
        const evtJson = safeJson(params.event);
        if (!evtJson.ok) {
            add(g, "omid.event_json", "Event JSON", "fail", "Invalid JSON in 'event' param");
        } else {
            evtData = evtJson.value;
        }
    }

    // 2. Specific Event Checks
    switch (flowType) {
        case "omidSessionStarted":
            if (evtData) {
                const data = evtData.data || {};
                const ctx = data.context || {};

                // Environment must be 'app' for mobile in-app ads. A non-app
                // environment means OMID viewability is measuring the wrong context
                // (misconfigured integration) — a real defect, so fail.
                if (ctx.environment === "app") add(g, "omid.env", "Environment", "pass", "app");
                else if (ctx.environment) add(g, "omid.env", "Environment", "fail", `Expected 'app' for in-app, got '${ctx.environment}'`);
                else add(g, "omid.env", "Environment", "warn", "Environment field missing");

                // AdSessionType: 'html' = AFMA/GMA banner, 'native' = native ads, 'javascript' = JS-managed
                // All three are valid IAB OMID session types — only flag truly unknown values
                const VALID_SESSION_TYPES = new Set(["html", "native", "javascript"]);
                if (VALID_SESSION_TYPES.has(ctx.adSessionType)) {
                    add(g, "omid.sessionType", "AdSessionType", "pass", ctx.adSessionType);
                } else if (ctx.adSessionType) {
                    add(g, "omid.sessionType", "AdSessionType", "warn", `Unknown adSessionType '${ctx.adSessionType}' — expected html/native/javascript`);
                } else {
                    add(g, "omid.sessionType", "AdSessionType", "warn", "adSessionType missing");
                }

                // VerificationParams presence — warn not fail; native ads may omit it
                if (data.verificationParameters) add(g, "omid.verifParams", "VerificationParams", "pass", "Present");
                else add(g, "omid.verifParams", "VerificationParams", "warn", "Missing — viewability vendor cannot verify session");

                // IDs check
                if (data.adSessionId) add(g, "omid.data.adSessionId", "Data.AdSessionId", "pass", "Present");
                else add(g, "omid.data.adSessionId", "Data.AdSessionId", "fail", "Missing in data object");
            } else {
                add(g, "omid.event_missing", "Event Data", "fail", "Missing 'event' param for SessionStarted");
            }
            break;

        case "omidImpression":
            if (evtData) {
                const data = evtData.data || {};
                // MediaType: should be 'display', 'video', or 'audio'
                const VALID_MEDIA_TYPES = new Set(["display", "video", "audio"]);
                if (data.mediaType) {
                    if (VALID_MEDIA_TYPES.has(data.mediaType)) {
                        add(g, "omid.mediaType", "Media Type", "pass", data.mediaType);
                    } else {
                        add(g, "omid.mediaType", "Media Type", "warn", `'${data.mediaType}' — expected display/video/audio`);
                    }
                } else {
                    add(g, "omid.mediaType", "Media Type", "warn", "Missing — IAB OMID requires mediaType on impression event");
                }

                // Viewport/adView presence — warn not fail
                if (data.viewport) add(g, "omid.viewport", "Viewport", "pass", "Present");
                else add(g, "omid.viewport", "Viewport", "warn", "Missing — may not be present on all OMID implementations");
            }
            break;

        case "omidMeasurementBelow":
        case "omidMeasurementAbove":
        case "omidViewable":
        case "omidMeasurementBelowViewable":
        case "omidMeasurementAboveViewable":
            if (evtData) {
                const data = evtData.data || {};
                const adView = data.adView || {};

                // PercentageInView — 0-100 range check
                if (adView.percentageInView != null) {
                    const pct = Number(adView.percentageInView);
                    if (pct < 0 || pct > 100) {
                        add(g, "omid.pctInView", "Percentage In View", "warn", `${pct}% — out of valid range (0-100)`);
                    } else {
                        // MRC viewability threshold is 50% for display. The value
                        // message stays the clean percentage (severity already conveys
                        // pass/warn); explain the threshold only when it's not met.
                        const mrcMet = pct >= 50;
                        add(g, "omid.pctInView", "Percentage In View", mrcMet ? "pass" : "warn",
                            mrcMet ? `${pct}%` : `${pct}% — below MRC 50% viewability threshold`);
                    }
                } else {
                    add(g, "omid.pctInView", "Percentage In View", "warn", "Missing — event data may not have been fully captured");
                }

                // Geometry presence + dimension sanity
                const checkGeo = (id, label, geo) => {
                    if (!geo) { add(g, id, label, "warn", "Missing"); return; }
                    const w = geo.width ?? geo.w, h = geo.height ?? geo.h;
                    if (w > 0 && h > 0) {
                        add(g, id, label, "pass", `${w}×${h} px at (${geo.x ?? "?"},${geo.y ?? "?"})`);
                    } else {
                        add(g, id, label, "warn", `Dimensions ${w}×${h} — unexpected zero/negative size`);
                    }
                };
                checkGeo("omid.geometry", "Geometry", adView.geometry);
                checkGeo("omid.onScreenGeometry", "OnScreenGeometry", adView.onScreenGeometry);
            }
            break;

        case "omidVideo":
            // Video events often have 'message' param describing the sub-event, OR a 'type' inside the event data
            // PDF says: "type=omidVideo ... &message=Video%20event... &event={... type:'loaded' ...}"
            if (evtData) {
                const vType = evtData.type; // sub-type like 'loaded', 'start', etc.
                add(g, "omid.videoType", "Video Event Type", "info", vType || "Unknown");

                if (vType === "loaded") {
                    const d = evtData.data || {};
                    add(g, "omid.video.skippable", "Skippable", "info", String(d.skippable));
                    add(g, "omid.video.autoPlay", "AutoPlay", "info", String(d.autoPlay));
                } else if (vType === "start") {
                    const d = evtData.data || {};
                    add(g, "omid.video.duration", "Duration", "info", String(d.duration));
                    add(g, "omid.video.volume", "Volume", "info", String(d.videoPlayerVolume));
                }
            }
            break;

        case "omidSupported":
        case "omidUnsupported":
            // Simple presence check passed
            add(g, "omid.support", "OMID Support", flowType === "omidSupported" ? "pass" : "warn",
                flowType === "omidSupported" ? "Supported" : "Unsupported");
            break;
    }

    return g;
}

//////////////////////////// Google Impression Beacon ////////////////////////////

// Impression beacons (pcs/activeview, gampad/adview, pcs/view) are NOT auction requests.
// They should be validated as lightweight delivery confirmations, not as ad requests.
// Key signals: HTTP 200, xai/sai attribution tokens, binary 1×1 pixel response (expected).
function googleImpressionChecks(req) {
    const g = mkGroup("Impression Beacon", "IMPRESSION");
    const status = Number(req.status ?? req.responseCode ?? 0);
    const url    = req.url || "";
    const params = qs(url);
    const respText = asText(req.responseBody);

    // HTTP status — beacon must succeed
    if (status >= 200 && status < 300) {
        add(g, "imp.status", "Beacon status", "pass", `HTTP ${status} — beacon delivered`);
    } else if (status === 0) {
        add(g, "imp.status", "Beacon status", "warn", "No response recorded");
    } else {
        add(g, "imp.status", "Beacon status", "fail", `HTTP ${status} — beacon failed`);
    }

    // Beacon subtype
    const subtype = url.includes("/pcs/activeview") ? "ActiveView (viewability)"
        : url.includes("/gampad/adview")            ? "Ad render (adview)"
        : url.includes("/pagead/adview")            ? "Ad render (pagead)"
        : url.includes("/pcs/view")                 ? "Impression (pcs/view)"
        : "Impression beacon";
    add(g, "imp.subtype", "Beacon type", "info", subtype);

    // xai / sai — ad attribution tokens. Present = beacon is properly linked to an ad interaction.
    // These are Google-encrypted and cannot be decoded, but their presence confirms attribution.
    const xai = params.xai || params.Xai;
    const sai = params.sai;
    if (xai || sai) {
        add(g, "imp.attribution", "Ad attribution (xai/sai)", "pass",
            xai ? `xai present — linked to ad request` : `sai present — linked to slot interaction`);
    } else {
        add(g, "imp.attribution", "Ad attribution (xai/sai)", "warn",
            "No xai/sai token — beacon may not be linked to a specific ad request");
    }

    // Binary response — EXPECTED for impression beacons (1×1 GIF pixel), not a problem
    if (looksBinaryString(respText)) {
        add(g, "imp.pixel", "Response (1×1 pixel)", "pass", "Binary pixel — correct for impression beacon");
    }

    // HTTPS
    add(g, "imp.https", "Secure (HTTPS)", isHttps(url) ? "pass" : "fail",
        isHttps(url) ? "Beacon sent over HTTPS" : "Beacon sent over HTTP — insecure");

    // Latency (if measured)
    if (req.durationMs != null) {
        add(g, "imp.latency", "Latency", req.durationMs <= 2000 ? "pass" : "warn",
            `${Math.round(req.durationMs)} ms`);
    }

    return g;
}

//////////////////////////// IMA DAI Stream Registration ////////////////////////////

/**
 * Validates IMA DAI stream registration requests (POST to dai.google.com/linear|vod/streams).
 * Proxy-side validateImaRequest.js does deep validation; this surfaces it structurally
 * and adds frontend-side cust_params token summary.
 */
function imaChecks(req) {
    const g = mkGroup("IMA DAI Stream Registration");

    // ── Parse body (JSON or form-encoded) ─────────────────────────────────────
    let body = {};
    try {
        const raw = req.requestBody || req.requestBodyParsed;
        if (raw) body = typeof raw === "object" ? raw : JSON.parse(raw);
    } catch {
        // form-encoded fallback
        try {
            const raw = req.requestBody || "";
            for (const pair of String(raw).split("&")) {
                const eq = pair.indexOf("=");
                if (eq < 0) continue;
                body[decodeURIComponent(pair.slice(0, eq))] = decodeURIComponent(pair.slice(eq + 1));
            }
        } catch {}
    }

    const adTagParams = body.adTagParameters || body.ad_tag_parameters || {};

    // ── DAI / api mode ─────────────────────────────────────────────────────────
    const daiMode = body.dai || body.api || adTagParams.api;
    if (daiMode) {
        const ok = daiMode === "dfpnonhosted";
        add(g, "ima.dai_mode", "DAI / api mode", ok ? "pass" : "warn",
            ok ? `api=${daiMode} ✓` : `api=${daiMode} — expected 'dfpnonhosted' for SSAI`);
    } else {
        add(g, "ima.dai_mode.missing", "DAI / api mode", "warn", "api field missing — expected 'dfpnonhosted' for SSAI");
    }

    // ── App identifier ─────────────────────────────────────────────────────────
    const appId = body.app || body.bundle || adTagParams.app;
    if (appId) {
        add(g, "ima.app", "App identifier", "pass", String(appId));
    } else {
        add(g, "ima.app.missing", "App identifier", "warn", "app/bundle identifier missing");
    }

    // ── cust_params token summary ──────────────────────────────────────────────
    // cust_params can be in body, adTagParameters, or URL query
    const cpRaw = body.cust_params || adTagParams.cust_params
        || qs(req.url).cust_params || "";
    const cpSrc = body.cust_params ? "body" : adTagParams.cust_params ? "adTagParameters" : "url";

    if (!cpRaw) {
        add(g, "ima.cust_params.missing", "cust_params", "fail",
            "Missing entirely — APS and SpringServe bid tokens cannot reach the SSAI auction");
    } else {
        add(g, "ima.cust_params.present", "cust_params", "pass", `Found in: ${cpSrc}`);

        // Decode and parse
        let cp = {};
        try {
            const dec = (() => { try { return decodeURIComponent(decodeURIComponent(cpRaw)); } catch { return decodeURIComponent(cpRaw); } })();
            for (const pair of dec.split("&")) {
                const eq = pair.indexOf("=");
                if (eq < 0) continue;
                cp[pair.slice(0, eq).trim()] = pair.slice(eq + 1).trim();
            }
        } catch {}

        // Gate against the active profile's declared stack — mirrors the proxy's
        // gating. hasAPSVideo/hasSpringServe are tri-state: false = this app never
        // integrates that demand source (skip the check entirely), null/undefined =
        // unknown (advisory info only), true = expected to be present (keep warn).
        const profile = getActiveProfile();
        const hasAPSVideo = profile?.hasAPSVideo;
        const hasSpringServeProfile = profile?.hasSpringServe;

        // Amazon APS token
        if (hasAPSVideo !== false) {
            const amznB = cp.amzn_b || cp["amzn_b"];
            const amznSlots = cp.amznslots;
            if (amznB || amznSlots) {
                add(g, "ima.amzn_b", "APS bid token (amzn_b)", "pass",
                    `amzn_b present${amznSlots ? " · amznslots present" : ""} — APS demand in SSAI auction`);
            } else {
                const sev = hasAPSVideo === true ? "warn" : "info";
                add(g, "ima.amzn_b.missing", "APS bid token (amzn_b)", sev,
                    "amzn_b absent from cust_params — APS C2S demand excluded from SSAI auction");
            }
        }

        // SpringServe token (some apps use ss_request_key + ss_ad_cache_key)
        if (hasSpringServeProfile !== false) {
            const ssBid = cp.ss_bid || cp.springserve_bid || cp.ss_token;
            const ssReqKey = cp.ss_request_key;
            const ssAdCacheKey = cp.ss_ad_cache_key;
            const hasSS = !!(ssBid || ssReqKey || ssAdCacheKey);
            if (hasSS) {
                const desc = ssBid ? `ss_bid=${ssBid.slice(0, 20)}…`
                    : ssReqKey ? `ss_request_key present${ssAdCacheKey ? " · ss_ad_cache_key ✓" : ""}`
                    : "SS token present";
                add(g, "ima.ss_bid", "SpringServe bid token", "pass", desc);
            } else {
                const sev = hasSpringServeProfile === true ? "warn" : "info";
                add(g, "ima.ss_bid.missing", "SpringServe bid token", sev,
                    "No SpringServe token in cust_params — SS demand excluded from SSAI auction");
            }
        }

        // cust_params summary table
        const cpKeys = Object.keys(cp);
        if (cpKeys.length > 0) {
            const columns = ["Key", "Value (truncated)"];
            const rows = cpKeys.map(k => [k, String(cp[k]).slice(0, 60) + (String(cp[k]).length > 60 ? "…" : "")]);
            pushTableRow(g, {
                id: "ima.cust_params.table",
                label: "cust_params key-values",
                severity: "pass",
                columns, rows,
                collapsed: true,
            });
        }
    }

    // ── SDK / version info ─────────────────────────────────────────────────────
    const sdkName = body.sdk_name || body.sdk || adTagParams.sdk_name;
    const sdkVer  = body.sdk_version || adTagParams.sdk_version;
    if (sdkName || sdkVer) {
        add(g, "ima.sdk", "IMA SDK", "pass", [sdkName, sdkVer].filter(Boolean).join(" "));
    }

    // ── PPID / rdid targeting signals ─────────────────────────────────────────
    const ppid = body.ppid || adTagParams.ppid;
    const rdid  = body.rdid || adTagParams.rdid;
    if (ppid) add(g, "ima.ppid", "PPID", "pass", `${String(ppid).slice(0, 16)}…`);
    if (rdid) add(g, "ima.rdid", "Device ID (rdid)", "pass", `${String(rdid).slice(0, 16)}…`);

    return g;
}

//////////////////////////// SpringServe (SS C2S / VAST) ////////////////////////////

/**
 * springserveChecks — dedicated validator for the SpringServe (Magnite) SSAI/C2S
 * path used for video ad decisioning. Two request shapes are validated:
 *
 *   springserve_ad_request  POST tv.springserve.com/bidr/   → OpenRTB C2S bid request
 *   springserve_vast        GET  tv|ad.springserve.com/ad/  → VAST ad response
 *
 * SS bid-token forwarding into the IMA stream registration is validated separately
 * in imaChecks (cust_params.ss_request_key); this card focuses on the SS request
 * and response themselves. Mirrors the structure of imaChecks / amazonChecks.
 *
 * Severity policy (matches the cross-client cleanup):
 *   - fail : structurally broken request (no imp[], not a VAST doc)
 *   - warn : real ad-quality problem (no-fill VAST, ad with no media)
 *   - info : advisory / expected states (no-bid, missing optional device fields)
 */
function springserveChecks(req) {
    const g = mkGroup("SpringServe (SS C2S / VAST)");
    const type = String(req.requestType || "").toLowerCase();
    const isBid  = type === "springserve_ad_request";
    const isVast = type === "springserve_vast";

    // ── C2S OpenRTB bid request (POST /bidr/) ─────────────────────────────────
    if (isBid) {
        const rq = safeJson(req.requestBody || req.requestBodyParsed);
        const rs = safeJson(req.responseBody);

        if (!rq.ok) {
            const bs = bodyState(req, 'request');
            // `trimmed` is a UI memory limit, not a broken request — never fail on it.
            add(g, "ss.req.json", "C2S OpenRTB body",
                rq.err === "empty" ? "warn" : "fail",
                rq.err === "empty" ? bs.message : `Invalid JSON: ${rq.err}`);
        } else {
            const b = rq.value || {};
            const imps = Array.isArray(b.imp) ? b.imp : [];
            add(g, "ss.req.imp", "OpenRTB imp[]", imps.length ? "pass" : "fail",
                imps.length ? `${imps.length} impression(s) requested`
                            : "No imp[] — not a valid OpenRTB bid request");

            const hasInventory = !!(b.app || b.site);
            add(g, "ss.req.inventory", "Inventory (app / site)", hasInventory ? "pass" : "warn",
                hasInventory ? (b.app ? "app object present" : "site object present")
                             : "Neither app nor site — inventory context missing");

            add(g, "ss.req.device", "Device object", b.device ? "pass" : "info",
                b.device ? "present" : "device object absent — targeting reduced");

            if (imps.length) {
                const hasVideo = imps.some(i => i.video);
                add(g, "ss.req.video", "Video impression", hasVideo ? "pass" : "info",
                    hasVideo ? "video object present on imp"
                             : "No video object on imp — expected for SSAI video");
            }

            // Privacy passthrough — ATT/NPA should reach SS so it can suppress PII targeting
            const lmt = b.device?.lmt;
            const npa = b.regs?.ext?.npa ?? b.user?.ext?.npa;
            const usp = b.regs?.ext?.us_privacy;
            const privacySignals = [
                lmt != null ? `lmt=${lmt}` : null,
                npa != null ? `npa=${npa}` : null,
                usp ? `us_privacy=${usp}` : null,
            ].filter(Boolean);
            add(g, "ss.req.privacy", "Privacy passthrough (lmt / npa / us_privacy)",
                privacySignals.length ? "pass" : "info",
                privacySignals.length ? privacySignals.join(" · ")
                                      : "No privacy signals forwarded to SpringServe");
        }

        // Bid outcome — did SpringServe return a usable bid / token?
        if (rs.ok) {
            const v = rs.value || {};
            const seatbid = v.seatbid;
            const hasBid = Array.isArray(seatbid) &&
                seatbid.some(s => Array.isArray(s.bid) && s.bid.length > 0);
            const firstBid = hasBid ? seatbid.find(s => Array.isArray(s.bid) && s.bid.length)?.bid?.[0] : null;
            const ssKey = v.ss_request_key || v.request_key
                || firstBid?.ext?.ss_request_key || firstBid?.ext?.ss_ad_cache_key;
            if (hasBid) {
                add(g, "ss.bid.result", "SpringServe bid result", "pass",
                    `Bid returned${firstBid?.price != null ? ` · price=${firstBid.price}` : ""}${ssKey ? ` · token present` : ""}`);
                if (!ssKey) {
                    add(g, "ss.bid.token", "SS bid token in response", "warn",
                        "Bid returned but no ss_request_key/ss_ad_cache_key — token cannot be forwarded to IMA");
                }
            } else {
                add(g, "ss.bid.result", "SpringServe bid result", "info",
                    "No bid — SpringServe returned no SS demand for this placement (normal when unsold)");
            }
        } else if (Number(req.status ?? req.responseCode) === 204) {
            add(g, "ss.bid.result", "SpringServe bid result", "info", "204 no-bid (no SS demand)");
        }
    }

    // ── VAST ad response (GET /ad/) ───────────────────────────────────────────
    if (isVast) {
        const xml = asText(req.responseBody);
        const status = Number(req.status ?? req.responseCode ?? 0);
        if (!xml || xml === "" || looksBinaryString(xml)) {
            if (status === 204 || status === 0) {
                add(g, "ss.vast.fill", "VAST fill", "warn", "Empty response (no-fill) — no creative available");
            } else {
                add(g, "ss.vast.body", "VAST response", "warn", "No VAST body captured");
            }
        } else if (!/<VAST[\s>]/i.test(xml)) {
            add(g, "ss.vast.shape", "VAST document", "warn", "Response is not a VAST document");
        } else {
            const adCount = (xml.match(/<Ad[\s>]/gi) || []).length;
            const isWrapper = /<Wrapper[\s>]/i.test(xml);
            const hasMedia = /<MediaFile[\s>]/i.test(xml) || /<VASTAdTagURI[\s>]/i.test(xml);
            if (adCount === 0) {
                add(g, "ss.vast.fill", "VAST fill", "warn",
                    "No-fill — VAST has no <Ad> element, no creative available for this placement");
            } else {
                add(g, "ss.vast.fill", "VAST fill", "pass",
                    `${adCount} ad(s) in VAST${hasMedia ? "" : " (no media — see below)"}`);
                add(g, "ss.vast.type", "VAST type", "info",
                    isWrapper ? "Wrapper — redirects to a downstream ad server" : "Inline creative");
                if (!hasMedia) {
                    add(g, "ss.vast.media", "MediaFile / Wrapper URI", "warn",
                        "Ad present but no <MediaFile> or <VASTAdTagURI> — creative may fail to render");
                }
                if (/<Error>\s*<!\[CDATA/i.test(xml)) {
                    add(g, "ss.vast.error_tag", "VAST Error tracking", "pass", "Error beacon URL present");
                }
                if (/<Impression>\s*<!\[CDATA/i.test(xml)) {
                    add(g, "ss.vast.impression_tag", "VAST Impression tracking", "pass", "Impression beacon URL present");
                }
            }
        }
    }

    // Unknown SS subtype with nothing to report — skip rather than show an empty card.
    if (!g.results.length) return null;
    return g;
}

//////////////////////////// type router & runner ////////////////////////////

export function inferType(req) {
    if (req.requestType) return String(req.requestType).toLowerCase();
    const u = (req.url || "").toLowerCase();

    // Check for OMID pings (id=omid_test or typical OMID format)
    if (u.includes("id=omid_test") || u.includes("type=omidsessionstarted")) return "omid";

    if (u.includes("/openrtb2/prebid")) return "prebid";
    // Impression / viewability beacons live on the SAME doubleclick hosts as ad
    // requests, so classify them BEFORE the generic "doubleclick -> google" rule —
    // otherwise /pagead/adview, /pcs/view, etc. get mis-validated as gampad/ads requests.
    // /pcs/activeview is the ActiveView measurement ping, not an impression (twin of proxy utils.js).
    if (u.includes("/pcs/activeview")) return "activeview_measurement";
    if (u.includes("/pcs/view") || u.includes("/gampad/adview") || u.includes("/pagead/adview")) return "google_impression";
    if ((u.includes("/gampad/ads") || u.includes("securepubads") || u.includes("doubleclick"))
        && !u.includes("/adview") && !u.includes("/pcs/")) return "google";
    if (u.includes("amazon-adsystem") || u.includes("/msdk/ads")) return "amazon";
    // robust context detection for api.strike.fox/ctx paths
    if (/\bapi\.strike\.fox\b/.test(u) && /(^|\/)ctx(\/|$)/.test(u)) return "context";
    if (u.includes("/configuration")) return "configuration";
    if (u.includes("/impression")) return "impression";
    if (u.includes("/click") || u.includes("/aclk")) return "click";

    return "generic";
}

function businessLogicChecks(req) {
    const g = mkGroup("Business Logic");
    const profile = getActiveProfile();

    // Skip all profile checks in generic mode
    if (profile.id === 'generic') return g;

    const rq = safeJson(req.requestBody);
    if (!rq.ok) return g;
    const b = rq.value || {};

    // 1. Publisher ID — only checked when profile specifies one
    if (profile.publisherId) {
        const pubId = b.app?.publisher?.id;
        if (pubId !== profile.publisherId) {
            add(g, "bus.pub_id", "Publisher ID", "warn",
                `Expected ${profile.publisherId} (${profile.name}), got ${pubId || "(missing)"}`);
        } else {
            add(g, "bus.pub_id", "Publisher ID", "pass", `Matches ${profile.publisherId}`);
        }
    }

    // 2. Bundle ID — only checked when profile specifies one
    if (profile.bundleId) {
        const bundle = b.app?.bundle;
        if (bundle !== profile.bundleId) {
            add(g, "bus.bundle", "Bundle ID", "warn",
                `Expected ${profile.bundleId} (${profile.name}), got ${bundle || "(missing)"}`);
        } else {
            add(g, "bus.bundle", "Bundle ID", "pass", `Matches ${profile.bundleId}`);
        }
    }

    // 3. Ad unit prefix — only checked when profile specifies one
    if (profile.adUnitPrefix) {
        try {
            const iu = new URL(req.url).searchParams.get('iu') || b.imp?.[0]?.tagid || '';
            if (iu && !iu.startsWith(profile.adUnitPrefix)) {
                add(g, "bus.iu_prefix", "Ad unit prefix", "warn",
                    `Expected to start with ${profile.adUnitPrefix}, got ${iu}`);
            }
        } catch {}
    }

    return g;
}

// Creative Audit (Heavy Ad + Malware Heuristics)
function creativeAuditChecks(req) {
    const g = mkGroup("Creative Audit");

    // 1. Heavy Ad Check
    const size = Number(req.size || req.responseBody?.length || 0);
    const kb = Math.round(size / 1024);

    // Skip small/empty responses (likely no-fill or tracking pixels)
    if (kb < 1) {
        add(g, "audit.size", "Creative Size", "pass", "Empty/Tiny (<1KB)");
    } else if (kb > 5000) {
        add(g, "audit.size", "Creative Size", "fail", `Critical: ${kb}KB (>5MB)`);
    } else if (kb > 150) {
        // IAB Standard for banner is typically 150KB-200KB; 150 is a strict warning threshold
        add(g, "audit.size", "Creative Size", "warn", `Heavy Ad: ${kb}KB (>150KB)`);
    } else {
        add(g, "audit.size", "Creative Size", "pass", `OK (${kb}KB)`);
    }

    // 2. Suspicious Script (Malware Heuristic)
    const body = asText(req.responseBody || "");
    if (!body || body.length < 50) return g; // skip tiny bodies

    const suspects = [];
    if (/eval\s*\(/.test(body)) suspects.push("eval()");
    if (/document\.write\s*\(/.test(body)) suspects.push("document.write()");
    if (/unescape\s*\(/.test(body)) suspects.push("unescape()");

    if (suspects.length > 0) {
        add(g, "audit.malware", "Suspicious Syntax", "warn", `Found: ${suspects.join(", ")}`);
    } else {
        add(g, "audit.malware", "Suspicious Syntax", "pass", "Clean (no eval/unescape)");
    }

    // 3. Obfuscation Heuristic (Long Lines)
    const lines = body.split(/\r?\n/);
    const longLines = lines.filter(l => l.length > 2000);
    if (longLines.length > 0) {
        add(g, "audit.obfuscation", "Minification/Obfuscation", "warn", `Found ${longLines.length} line(s) > 2000 chars`);
    }

    return g;
}

//////////////////////////// IU Taxonomy ////////////////////////////

/**
 * Validates the GAM ad unit path (iu param) against the known app taxonomy.
 *
 * Page detection priority:
 *   1. User-saved override in localStorage (URL prefix → page code)
 *   2. Auto-detection via pageFromUrl() using the request's content_url
 *   3. No expectedPage (taxonomy structure still validated)
 *
 * When a mismatch is found via auto-detection (not a saved override), we
 * emit a special result with `confirmable: true` so the UI can ask the user
 * to confirm and save the correct mapping.
 */
function iuTaxonomyGroup(req) {
  const g = mkGroup('IU Taxonomy');
  const params = qs(req.url);
  const iu = params.iu || '';

  if (!iu) {
    add(g, 'iu.missing', 'Ad unit (iu) present', 'fail', 'No iu param found in GAM request');
    return g;
  }

  // ── Detect app ──
  const detected = detectApp(iu);
  if (!detected) {
    add(g, 'iu.unknown_app', 'App taxonomy match', 'warn',
      `IU does not match any known taxonomy: ${iu}`);
    return g;
  }
  add(g, 'iu.app', 'App taxonomy match', 'pass', `Matched: ${detected.cfg.label}`);

  // ── Determine expected page code ──
  const contentUrl = params.content_url || '';
  let expectedPage = null;
  let pageSource = null; // 'override' | 'auto' | null
  let overrideKey = null;

  if (contentUrl) {
    // Priority 1: saved override
    const saved = lookupPageOverride(contentUrl);
    if (saved) {
      expectedPage = saved.pageCode;
      pageSource = 'override';
      overrideKey = saved.key;
      add(g, 'iu.page_source', 'Page detection', 'pass',
        `Using saved override for "${saved.label}": page = "${expectedPage}"`);
    } else {
      // Priority 2: auto-detect from URL
      const autoPage = pageFromUrl(contentUrl, detected.cfg);
      if (autoPage) {
        expectedPage = autoPage;
        pageSource = 'auto';
        add(g, 'iu.page_source', 'Page detection', 'pass',
          `Auto-detected from URL: page = "${expectedPage}"`);
      } else {
        add(g, 'iu.page_source', 'Page detection', 'warn',
          `Could not determine page type from content_url: ${contentUrl}`);
      }
    }
  } else {
    add(g, 'iu.no_content_url', 'content_url present', 'warn',
      'No content_url in GAM request — cannot verify page type match');
  }

  // ── Run taxonomy validation ──
  // When expected page came from a saved override the user has already confirmed it —
  // don't flag the IU's page segment as "unknown" even if it's not in the stock taxonomy.
  const issues = validateIU(iu, { expectedPage, allowUnknownPage: pageSource === 'override' });

  if (issues.length === 0) {
    add(g, 'iu.valid', 'IU structure valid', 'pass', `IU OK: ${iu}`);
    return g;
  }

  // Map taxonomy issues → validator results
  issues.forEach((issue, idx) => {
    const severity = issue.level === 'fail' ? 'fail' : issue.level === 'warn' ? 'warn' : 'pass';
    const isPageMismatch = issue.code === 'iu_page_mismatch';

    // For page mismatches that came from AUTO-detection (not a saved override),
    // mark as confirmable so the UI can ask the user to verify and save.
    const confirmable = isPageMismatch && pageSource === 'auto';

    g.results.push({
      id: `iu.${issue.code}_${idx}`,
      label: isPageMismatch ? 'Page type mismatch' : 'IU validation',
      severity,
      message: issue.msg,
      // Extra fields consumed by the validate panel UI:
      confirmable,
      confirmData: confirmable ? {
        contentUrl,
        detectedPage: expectedPage,
        urlKey: urlToKey(contentUrl),
        // Pass the page segment from the IU so the banner can pre-fill it as custom
        iuPage: issue.code === 'iu_page_mismatch' ? (() => {
          try { return iu.replace(/^\//, '').split('/').filter(Boolean)[3] || ''; } catch { return ''; }
        })() : '',
      } : undefined,
    });
  });

  return g;
}

// ─── Profile Config Validator ─────────────────────────────────────────────────
// After initialization, flags any IU, Prebid stored request ID, or Amazon slot
// UUID seen in live traffic that was NOT present in the saved baseline / profile.
// Only runs when the active profile has been initialized (has a saved baseline).
//
// Severity:
//   fail  — unknown Prebid stored request ID (could be a wrong config or rogue call)
//   warn  — unknown GAM IU (new slot, could be intentional)
//   warn  — unknown Amazon slot UUID (new slot ID)

function profileConfigChecks(req) {
    const profile  = getActiveProfile();
    if (!profile || profile._isFallback) return null;   // no profile — skip

    const baseline = loadBaseline(profile.appId, profile.profileId);
    // Also accept the profile's own prebidMappings as a known-IDs source
    const knownPrebidIds = new Set([
        ...Object.values(profile.prebidMappings || {}),
        ...Object.values(baseline?.prebidMappings || {}),
    ]);
    const knownIUs = new Set([
        ...(baseline?.iuRows?.map(r => r.iu) || []),
    ]);
    const knownSlots = new Set([
        ...Object.keys(profile.amazonSlotIds || {}),
        ...Object.keys(baseline?.amazonSlots || {}),
    ]);

    // Nothing configured yet — don't flag anything
    if (knownPrebidIds.size === 0 && knownIUs.size === 0 && knownSlots.size === 0) return null;

    const results = [];
    const url = req?.url || '';
    const t   = req?.requestType || req?.type || '';

    // ── GAM: unknown IU ──────────────────────────────────────────────────────
    if (t === 'google' && knownIUs.size > 0) {
        try {
            const iuParam = new URLSearchParams(url.split('?')[1] || '').get('iu');
            const iu = iuParam ? decodeURIComponent(iuParam) : null;
            if (iu && !knownIUs.has(iu)) {
                results.push({
                    id: 'profile_unknown_iu',
                    label: 'Unknown ad unit',
                    severity: 'warn',
                    message: `IU not seen during initialization: ${iu} — new slot or wrong placement`,
                });
            }
        } catch {}
    }

    // ── Prebid: unknown stored request ID ────────────────────────────────────
    // Use GPID (Global Placement ID) from imp.ext.gpid to identify which slot this
    // is — gives a human-readable slot name in the error message.
    if (t === 'prebid' && knownPrebidIds.size > 0) {
        try {
            const body = (() => {
                const raw = req?.requestBodyParsed || req?.requestBody;
                if (!raw) return null;
                return typeof raw === 'object' ? raw : JSON.parse(raw);
            })();
            const imps = Array.isArray(body?.imp) ? body.imp : [];
            imps.forEach((imp, idx) => {
                const storedId = imp?.ext?.prebid?.storedrequest?.id;
                if (!storedId) return;
                if (!knownPrebidIds.has(storedId)) {
                    // Get GPID for a descriptive slot name
                    const gpid = imp?.ext?.gpid || imp?.ext?.data?.pbadslot || imp?.ext?.data?.adUnitCode || null;
                    const slotDesc = gpid ? ` (slot: ${gpid})` : '';
                    results.push({
                        id: `profile_unknown_prebid_${idx}`,
                        label: 'Unknown Prebid stored request ID',
                        severity: 'fail',
                        message: `Stored request ID "${storedId}"${slotDesc} was not in the initialized config — wrong config or unexpected call`,
                    });
                }
            });
        } catch {}
    }

    // ── Amazon APS: unknown slot UUID ────────────────────────────────────────
    if (t === 'amazon' && knownSlots.size > 0) {
        try {
            const body = (() => {
                const raw = req?.requestBodyParsed || req?.requestBody;
                if (!raw) return null;
                return typeof raw === 'object' ? raw : JSON.parse(raw);
            })();
            const vd   = req?.validData || {};
            const uuid = body?.slots?.[0]?.slot || vd?.slots?.[0]?.slot || vd?.slot;
            if (uuid && !knownSlots.has(uuid)) {
                results.push({
                    id: 'profile_unknown_slot',
                    label: 'Unknown Amazon slot UUID',
                    severity: 'warn',
                    message: `Slot "${uuid.slice(0, 18)}…" not in profile — new slot ID or wrong app build`,
                });
            }
        } catch {}
    }

    if (!results.length) return null;

    return {
        title:   '⚠ Profile Config Mismatch',
        scope:   'PROFILE',
        results,
        _profileDriven: true,
    };
}

// Impression/click beacon types — should NOT run commonChecks (privacy signals, binary body
// warnings etc. are meaningless for 1×1 pixel beacons and generate false failures).
const BEACON_TYPES = new Set([
    "google_impression", "omid_impression", "omid_measurement", "omid_session",
    "omid_video", "omid_test_beacon", "amazon_impression", "prebid_win",
    "bidder_impression", "impression", "click", "google_click",
    // /pcs/activeview moved here from google_impression (viewability WP1): still a 1x1 beacon.
    "activeview_measurement",
]);

/**
 * privacyChecks — surfaces the TCF purpose/vendor consent map and decoded GPP
 * sections that the proxy attaches to validData.ortb.privacy. Turns the raw
 * consent string into readable pass/warn rows so a tester can see exactly which
 * purposes/vendors are denied and which state opt-outs are signalled.
 */
function privacyChecks(req) {
    const pv = req?.validData?.ortb?.privacy;
    if (!pv) return null;
    const g = mkGroup('Privacy — TCF / GPP consent');

    const tcf = pv.tcf;
    if (tcf) {
        if (tcf.meta?.cmpId) {
            add(g, 'tcf.cmp', 'TCF CMP', 'pass',
                `CMP ${tcf.meta.cmpId} · vendorList v${tcf.meta.vendorListVersion ?? '?'}` +
                (tcf.meta.lastUpdated ? ` · updated ${String(tcf.meta.lastUpdated).slice(0, 10)}` : ''));
        }
        Object.values(tcf.purposeMap || {}).forEach(p => {
            add(g, `tcf.p${p.id}`, `TCF Purpose ${p.id}`, p.granted ? 'pass' : 'warn',
                `${p.name}: ${p.granted ? 'consent granted' : 'denied'}`);
        });
        Object.entries(tcf.vendorMap || {}).forEach(([k, v]) => {
            add(g, `tcf.v.${k}`, v.name, v.granted ? 'pass' : 'warn',
                `Vendor ${v.id}: ${v.granted ? 'consent granted' : 'no consent — ads may be blocked'}`);
        });
    }

    const gpp = pv.gpp;
    if (gpp && gpp.detected) {
        if (gpp.decoded) {
            (gpp.sections || []).forEach(s => {
                const sev = (s.issues && s.issues.length) ? 'warn' : 'pass';
                add(g, `gpp.${s.id}`, s.label, sev, s.summary || 'present');
            });
        } else {
            add(g, 'gpp.struct', 'GPP', 'pass',
                `GPP present (${(gpp.sections || []).length} section(s)) — install @iabgpp/cmpapi for full decode`);
        }
    }

    return g.results.length ? g : null;
}

export function runValidations(req, ctx = {}) {
    const groups = [];
    const t = inferType(req);

    // commonChecks only for real ad requests — beacons have their own validators
    if (!BEACON_TYPES.has(t)) {
        groups.push(commonChecks(req));
    }

    if (t === "prebid") {
        groups.push(prebidChecks(req, ctx), ortbSchemaChecks(req), businessLogicChecks(req));
        const privacyGroup = privacyChecks(req);
        if (privacyGroup) groups.push(privacyGroup);
    } else if (t === "google") {
        groups.push(gampadChecks(req, ctx), iuTaxonomyGroup(req));
        // WP4a seam: pass the active profile's rules explicitly instead of
        // relying on custParamsRulesCheck's internal getActiveProfile() default —
        // this is the call site that would carry `rules` in a POST body server-side.
        const cpRulesGam = custParamsRulesCheck(req, t, getActiveProfile().custParamRules);
        if (cpRulesGam) groups.push(cpRulesGam);
    } else if (t === "ima") {
        groups.push(imaChecks(req));
        const cpRulesIma = custParamsRulesCheck(req, t, getActiveProfile().custParamRules);
        if (cpRulesIma) groups.push(cpRulesIma);
    } else if (t === "springserve_ad_request" || t === "springserve_vast") {
        const ss = springserveChecks(req);
        if (ss) groups.push(ss);
    } else if (t === "amazon") {
        groups.push(amazonChecks(req, ctx));
    } else if (t === "amazon_video") {
        groups.push(amazonVideoChecks(req));
    } else if (t === "context") {
        groups.push(contextChecks(req));
    } else if (t === "configuration") {
        groups.push(configurationChecks(req));
    } else if (t === "google_impression") {
        groups.push(googleImpressionChecks(req));
    } else if (t === "click" || t === "impression" || t === "google_click") {
        groups.push(trackingChecks(req));
    } else if (t === "omid" || t === "omid_impression" || t === "omid_measurement" ||
               t === "omid_session" || t === "omid_video" || t === "omid_test_beacon") {
        groups.push(omidChecks(req));
    } else if (t === "activeview_measurement") {
        groups.push(googleImpressionChecks(req)); // ActiveView beacons share the same beacon validator
    }

    // ── Knowledge Base requirements (offline, no AI) ──────────────────────────
    // Map the inferred type to the KB requestType vocabulary
    const kbTypeMap = {
        google:            'google',
        prebid:            'prebid',
        amazon:            'amazon',
        amazon_video:      'amazon_video',
        ima:               'ima',
        gam_video:         'gam_video',
        vast_tracking:     'vast_tracking',
        springserve_vast:  'springserve_vast',
        springserve_ad_request: 'springserve_ad_request',
        google_impression: 'google_impression',
        google_click:      'google_click',
    };
    const kbType = kbTypeMap[t] || kbTypeMap[req?.requestType] || null;
    if (kbType) {
        try {
            const kbGroup = runKBRules(req, kbType);
            if (kbGroup) {
                // Deduplicate: collect parameter paths already covered by code-level checks
                const coveredParams = new Set(
                    groups.flatMap(g => g.results || [])
                          .filter(r => r._parameter)
                          .map(r => r._parameter)
                );
                // Only include KB checks for params not already validated above,
                // but always include failures (they may be a second opinion that matters)
                const deduped = kbGroup.results.filter(r =>
                    r.severity !== 'pass' || !coveredParams.has(r._parameter)
                );
                if (deduped.length) groups.push({ ...kbGroup, results: deduped });
            }
        } catch (e) {
            // KB evaluation should never crash the validator — degrade silently
            console.warn('[RuleEngine] evaluation error:', e?.message);
        }
    }

    // ── Standard checks (global, unified schema) ──────────────────────────────
    // STANDARD_RULESET is the app-agnostic GAM/Prebid/privacy/OMID baseline that
    // runs for EVERY profile. Any rules persisted on prof.ruleSet (legacy, from the
    // retired Rules editor) are merged in and deduped by id so nothing double-fires.
    // Each rule self-filters by request type/page, so this is safe on all types.
    // Additive — never blocks the built-in checks above; degrades silently on error.
    try {
        const prof = getActiveProfile();
        const legacy = (prof && Array.isArray(prof.ruleSet)) ? prof.ruleSet : [];
        const seen = new Set();
        const merged = [];
        for (const r of [...STANDARD_RULESET, ...legacy]) {
            if (r && r.id) {
                if (seen.has(r.id)) continue;
                seen.add(r.id);
            }
            merged.push(r);
        }
        if (merged.length) {
            const stdGroup = runRuleSet(merged, req, t, 'Standard Checks');
            if (stdGroup && stdGroup.results.length) {
                // Dedup: drop a Standard PASS row when the same parameter is already
                // validated elsewhere — either by a code-level validator (curated map
                // below, since those results don't tag _parameter) or by a prior group
                // that DOES tag _parameter (KB / cust_params). Warnings and failures
                // are always kept as a second opinion (mirrors the KB dedup policy).
                const CODE_COVERED = {
                    google: new Set(['iu', 'sz', 'correlator', 'gdfp_req', 'us_privacy']),
                    prebid: new Set(['imp']),
                };
                const covered = new Set(CODE_COVERED[t] || []);
                for (const gr of groups) {
                    for (const r of (gr.results || [])) {
                        if (r._parameter) covered.add(r._parameter);
                    }
                }
                const deduped = stdGroup.results.filter(r =>
                    String(r.severity).toLowerCase() !== 'pass' || !covered.has(r._parameter)
                );
                if (deduped.length) groups.push({ ...stdGroup, results: deduped });
            }
        }
    } catch (e) {
        console.warn('[ruleSet] evaluation error:', e?.message);
    }

    // Creative Audit (Generic for all ad-like requests that have content)
    const hasBody = req.responseBody && String(req.responseBody).length > 20;
    if (hasBody && (t === "google" || t === "prebid" || t === "amazon" || t === "generic")) {
        groups.push(creativeAuditChecks(req));
    }

    // ── Profile config mismatch (unknown IU / Prebid ID / Amazon slot) ────────
    if (t === 'google' || t === 'prebid' || t === 'amazon') {
        try {
            const profileGroup = profileConfigChecks(req);
            if (profileGroup) groups.push(profileGroup);
        } catch {}
    }

    // Proxy/server issues (from backend)
    const proxyResults = proxyIssuesChecks(req);
    if (proxyResults.length)
        groups.push({
            title: "Proxy (server) issues",
            scope: "SYSTEM",
            results: proxyResults,
        });

    // summarize — normalize severities so advisory 'info' items are NOT counted
    // as failures (which would wrongly flip overall → fail and tank the score).
    let pass = 0, warn = 0, fail = 0, info = 0, rec = 0;
    for (const g of groups) {
        for (const r of g.results) {
            const s = String(r.severity || '').toLowerCase();
            if (s === 'pass' || s === 'ok' || s === 'success') pass++;
            else if (s === 'rec') rec++; // WP1 'recommend' tier — advisory only, MUST stay out of fail/warn/info and overall/score math
            else if (s === 'info' || s === 'information' || s === 'recommendation' || s === 'notice' || s === 'optional') info++;
            else if (s === 'warn' || s === 'warning' || s === 'recommended') warn++;
            else fail++; // 'fail' | 'error' | 'required' | unknown
        }
    }
    const overall = fail > 0 ? "fail" : (warn > 0 ? "warn" : "pass");
    // Info/rec items are advisory — excluded from the score denominator.
    const score = Math.round((pass / (pass + warn + fail || 1)) * 100);

    return { summary: { pass, warn, fail, info, rec, overall, score }, groups };
}

export default runValidations;
